/**
 * BowlingSceneComponent — the entire 3D bowling scene + physics +
 * input + per-scene UI overlays. Renders the alley (lane, gutters,
 * walls, ceiling, pinsetters, doodle far-end wall, two ghost lanes
 * with screen-saver displays), the live pin rack, the player's ball,
 * and the in-scene CRT score panel above the pin deck. Owns:
 *
 *   - Three.js renderer / scene / camera / lights / texture caches
 *   - cannon-es physics world / static bodies (lane, walls, pit) /
 *     dynamic bodies (10 pins + 1 ball) / contact materials
 *   - Pointer event handling (drag-to-position + tap-and-hold to
 *     charge + release-to-bowl, two-step gesture)
 *   - Power-bar UI + sweet-spot / overcharge logic
 *   - Camera state machine (idle / follow / hold)
 *   - Settle detection + post-impact `rollComplete` emit
 *   - Audio: looping ball-roll (cut on impact) + pin-hit one-shot
 *   - On-lane swipe / tap hint cues + visibility lifecycle
 *   - Live CRT score panel + ghost-lane "screen saver" canvases
 *
 * Inputs from the host:
 *   - `currLang`        — bilingual UI labels
 *   - `pinsStanding`    — 10-bool mask of pins to spawn standing
 *   - `rackTick`        — bump triggers re-rack + ball reset
 *   - `inputDisabled`   — blocks pointer input during banners
 *   - `scoreboardData`  — fed to the in-scene CRT score panel
 *
 * Events to the host:
 *   - `ready`         — async load complete, scene ready
 *   - `rollStarted`   — ball just left the foul line
 *   - `rollComplete`  — pins settled, knocked count + indices
 */

import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener,
  Input, NgZone, OnDestroy, Output, ViewChild
} from '@angular/core';
import { BowlingEngineService } from '../../../_services/bowling-engine.service';
import { GameAudioService } from '../../../_services/game-audio.service';

// ── Lane geometry constants ────────────────────────────────────────
// Lane length is per spec (regulation 60 ft / 18.29 m). Other
// dimensions are scaled **larger than regulation** so the scene
// reads well on a small canvas without tiny pins / a hairline lane.
// Masses stay per spec (7 kg ball / 1.5 kg pin) so the physics impact
// feels right.
const LANE_LENGTH = 18.29;            // 60 ft regulation lane (per spec)
const LANE_WIDTH = 1.6;               // wider than regulation (1.054) for visibility
const APPROACH_LENGTH = 2.0;          // visual approach depth behind the foul line
const GUTTER_WIDTH = 0.30;            // visual gutter width per side (no physics — gutters are decorative)
const FOUL_LINE_Z = LANE_LENGTH / 2;  // +9.145 — front of lane (where ball starts)
const PIN_HEAD_Z = -FOUL_LINE_Z + 1.2; // headpin 1.2 m before back of lane
const BALL_START_Z = FOUL_LINE_Z - 1.5; // ball idles 1.5 m inside the lane from the front edge
const BALL_RADIUS = 0.16;             // larger than regulation for on-screen presence
const BALL_MASS = 7;                  // per spec (kg)
const PIN_HEIGHT = 0.48;              // larger than regulation (15 in / 0.381 m) for visibility
const PIN_RADIUS = 0.085;             // larger than regulation (~2.4 in radius) for visibility
const PIN_MASS = 1.5;                 // per spec (kg)
const PIN_ROW_SPACING = 0.30;         // depth between pin rows — proportional to scaled-up pins
const PIN_COL_SPACING = 0.18;         // lateral spacing between pins in the same row

// Camera lingers on the pin deck after the settle. Synced with the
// parent's banner timeout (1400 ms) so the camera, the banner display,
// and the rack-up all finish together — instead of running back-to-back
// for several seconds of dead time.
const HOLD_AFTER_SETTLE_MS = 1400;

// ── Power-bar / shot-charge tuning ──────────────────────────────────────
// Time for the bar to climb 0% → 100%. Calibrated so a player has time to
// see the colour zones tick by but isn't waiting around — a confident
// release at the sweet spot sits ~1.5 s after pointer-down.
const BAR_FILL_MS = 1800;
const SWEET_SPOT_LO = 0.80;          // sweet spot: 80%–95% of bar height
const SWEET_SPOT_HI = 0.95;
// Speed mapping (m/s along -Z). MIN at 0% charge, MAX_NORMAL at the top
// of the pre-sweet zone, PERFECT for sweet-spot releases (disproportionate
// bonus), FOUL for an overcharged 100% release. Past the sweet spot but
// not yet 100%, speed drops linearly from MAX_NORMAL toward 0.4 × MAX_NORMAL.
const SHOT_MIN_SPEED = 7;
const SHOT_MAX_NORMAL_SPEED = 16;
const SHOT_PERFECT_SPEED = 20;
const SHOT_FOUL_SPEED = 5;

// Pit (dark sunken area behind the pin deck — pins/ball fall into it).
const PIT_FRONT_Z = -LANE_LENGTH / 2;        // -9.145, where the lane ends
const PIT_BACK_Z = PIT_FRONT_Z - 1.6;        // -10.745, back of the pit
const PIT_DEPTH = 0.5;                       // pit floor sits 0.5 m below lane top

// Alley shell envelope — used by both the alley environment (walls/floor/
// ceiling) and the lane (pit visuals span the full alley width).
const ALLEY_HALF_WIDTH = 5.5;                // distance from centre to side wall
const ALLEY_HEIGHT = 3.8;                    // ceiling height

/**
 * Standard 10-pin triangle layout — `[row, col]` offsets into the
 * pin grid. Row 0 is the front pin (headpin, single), row 3 is the
 * back row (4 pins). `col` is signed so the triangle is centred on
 * the lane (col=0 = lane centre). Each pin is positioned at:
 *   `x = col * PIN_COL_SPACING`
 *   `z = PIN_HEAD_Z - row * PIN_ROW_SPACING`
 *
 *     row 0:           [0]               (1 pin, headpin)
 *     row 1:        [-1] [+1]            (2 pins)
 *     row 2:     [-2]  [0]  [+2]         (3 pins)
 *     row 3:  [-3] [-1] [+1] [+3]        (4 pins, back row)
 */
const PIN_LAYOUT: Array<[row: number, col: number]> = [
  [0, 0],
  [1, -1], [1, 1],
  [2, -2], [2, 0], [2, 2],
  [3, -3], [3, -1], [3, 1], [3, 3]
];

/**
 * Per-pin runtime tuple — keeps the visual mesh, the cannon body,
 * the original spawn pose (for re-rack), the knocked flag (so
 * settle-detect skips already-down pins), and the index 0..9 used
 * to report knocked-pin lists in the `rollComplete` event.
 */
interface Pin {
  /** Three.js Group containing the pin mesh + any decoration. The
   *  group origin == body centre so position copy is one-shot. */
  mesh: any;
  /** Cannon dynamic Body — Box shape (NOT Cylinder; see `buildPins`
   *  for the rationale — Cylinder vs Plane has a known contact bug). */
  body: any;
  /** Initial spawn pose for `restorePinsFromMask` — pin returns
   *  here when a fresh rack is requested (rackTick bump). */
  spawn: { x: number; y: number; z: number };
  /** True after this pin has been counted as knocked in the current
   *  round. Reset on rack via `restorePinsFromMask`. Used by both
   *  `isPinDown` (skip already-counted) and `checkSettle`'s
   *  pinsAtRest scan (knocked pins skipped so a wobbling downed pin
   *  doesn't gate settle). */
  knocked: boolean;
  /** Stable index 0..9 — passed to the host in `rollComplete.knockedIndices`. */
  idx: number;
}

/**
 * Camera state machine — three modes:
 *   - `idle`   — static behind-the-bowler shot at start position.
 *   - `follow` — lerps to track the ball as it travels down the lane.
 *   - `hold`   — pin-deck framing for the post-impact reveal (lerp-
 *                in from wherever follow left off).
 *
 * Transitions:
 *   `idle` → `follow` on `launch()` (ball just left the foul line).
 *   `follow` → `hold` on `beginHold()` (settle detected).
 *   `hold` → `idle` on `finishHold()` (after `HOLD_AFTER_SETTLE_MS`)
 *            OR on `rackTick` change (parent forced a rack).
 */
type CamState = 'idle' | 'follow' | 'hold';

@Component({
  selector: 'app-bowling-scene',
  template: `
    <div class="scene-wrap">
      <canvas #canvas class="bowling-canvas"></canvas>
      <!-- Step 1: position hint — two hand-arrow cues flanking the
           ball (no centered black box, no overlap on the ball). The
           ball sits at the bottom-centre of the canvas at idle, so
           left/right cues at ~30 % from each side land just outside
           the ball without obscuring it. -->
      <div class="lane-hint lane-hint--swipe"
           *ngIf="loaded && !rolling && !holding && !charging && !positioned && hintsActive">
        <div class="lane-hint-side lane-hint-side--left">
          <span class="arrow">‹</span>
          <span class="hand">👆</span>
        </div>
        <div class="lane-hint-side lane-hint-side--right">
          <span class="hand">👆</span>
          <span class="arrow">›</span>
        </div>
        <div class="lane-hint-text">
          {{ currLang === 'ar' ? 'اسحب لتحريك الكرة' : 'SWIPE TO MOVE' }}
        </div>
      </div>

      <!-- Step 2: charge hint — single tapping hand above the ball
           with an expanding ripple, plus "HOLD" text. Same on-lane
           transparent feel as the swipe hint. -->
      <div class="lane-hint lane-hint--tap"
           *ngIf="loaded && !rolling && !holding && !charging && positioned && hintsActive">
        <div class="lane-hint-tap">
          <span class="ripple"></span>
          <span class="hand">👆</span>
        </div>
        <div class="lane-hint-text">
          {{ currLang === 'ar' ? 'اضغط مطولًا واترك' : 'TAP &amp; HOLD' }}
        </div>
      </div>
      <div class="power-bar" *ngIf="charging">
        <div class="bar-label">
          {{ currLang === 'ar' ? 'القوة' : 'POWER' }}
        </div>
        <div class="bar-track">
          <div class="bar-sweet"></div>
          <div class="bar-fill"
               [style.height.%]="chargeLevel * 100"
               [class.zone-low]="chargeLevel < 0.80"
               [class.zone-sweet]="chargeLevel >= 0.80 && chargeLevel <= 0.95"
               [class.zone-over]="chargeLevel > 0.95">
          </div>
        </div>
      </div>
      <div class="loading" *ngIf="!loaded">
        <div class="spinner"></div>
        <span>{{ currLang === 'ar' ? 'جارٍ تحميل المشهد...' : 'Loading scene...' }}</span>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; height: 100%; }
    .scene-wrap { position: relative; width: 100%; height: 100%; }
    .bowling-canvas {
      width: 100%;
      height: 100%;
      display: block;
      border-radius: 12px;
      background: #0d0d1f;
      touch-action: none;
      cursor: grab;
    }
    .bowling-canvas.grabbing { cursor: grabbing; }
    /* On-lane hint overlays — sit transparent on the canvas (no black
       box, no backdrop). Two side cues for swipe / position; a single
       cue above the ball for tap-and-hold / charge. The ball sits
       roughly at the bottom-centre of the canvas at idle, so the
       cues are placed to flank or hover above it without obscuring. */
    .lane-hint {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 4;
      animation: laneHintIn 0.3s ease-out;
    }
    @keyframes laneHintIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    .lane-hint .hand {
      font-size: 38px;
      line-height: 1;
      filter: drop-shadow(0 0 8px rgba(255, 215, 74, 0.6))
              drop-shadow(0 2px 6px rgba(0, 0, 0, 0.5));
    }
    .lane-hint .arrow {
      font-size: 44px;
      font-weight: 900;
      color: #FFD54A;
      text-shadow: 0 0 10px rgba(255, 215, 74, 0.7),
                   0 2px 6px rgba(0, 0, 0, 0.5);
      line-height: 1;
    }
    .lane-hint-text {
      position: absolute;
      bottom: 4%;
      left: 50%;
      transform: translateX(-50%);
      color: #FFD54A;
      font-size: 18px;
      font-weight: 900;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.85),
                   0 0 14px rgba(255, 215, 74, 0.55);
      white-space: nowrap;
      animation: laneHintTextPulse 1.4s ease-in-out infinite;
    }
    @keyframes laneHintTextPulse {
      0%, 100% { opacity: 0.85; }
      50%      { opacity: 1; }
    }

    /* Swipe variant — left + right hand-arrow cues flanking the ball.
       The ball renders at the bottom-centre of the canvas at idle, so
       the cues are anchored to the canvas centre with a fixed pixel
       offset (≈ 60 px) — they sit *right next to* the ball regardless
       of canvas width, never drifting out toward the gutters on a
       wider canvas. Each side bobs outward to telegraph the drag
       direction. */
    .lane-hint-side {
      position: absolute;
      bottom: 14%;
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .lane-hint-side--left {
      right: calc(50% + 60px);   /* right edge of cue sits 60 px left of canvas centre */
      animation: bobLeft 1.3s ease-in-out infinite;
    }
    .lane-hint-side--right {
      left: calc(50% + 60px);    /* left edge of cue sits 60 px right of canvas centre */
      animation: bobRight 1.3s ease-in-out infinite;
    }
    @keyframes bobLeft {
      0%, 100% { transform: translateX(0); }
      50%      { transform: translateX(-12px); }
    }
    @keyframes bobRight {
      0%, 100% { transform: translateX(0); }
      50%      { transform: translateX(12px); }
    }

    /* Tap variant — single hand right at the ball with an expanding
       ripple ring. Centred horizontally, just above ball-level so
       the ripple visually wraps the ball. */
    .lane-hint-tap {
      position: absolute;
      left: 50%;
      bottom: 18%;
      transform: translateX(-50%);
    }
    .lane-hint-tap .hand {
      display: inline-block;
      animation: handTap 1.1s ease-in-out infinite;
    }
    .lane-hint-tap .ripple {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 56px;
      height: 56px;
      border: 2.5px solid #FFD54A;
      border-radius: 50%;
      animation: rippleExpand 1.1s ease-out infinite;
    }
    @keyframes handTap {
      0%, 100% { transform: scale(1); }
      55%      { transform: scale(0.78); }
      70%      { transform: scale(1); }
    }
    @keyframes rippleExpand {
      0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
      40%  { opacity: 0.85; }
      100% { opacity: 0; transform: translate(-50%, -50%) scale(2); }
    }
    .loading {
      position: absolute; inset: 0;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      gap: 12px;
      color: rgba(255,255,255,0.75);
      font-size: 13px;
      background: rgba(13, 13, 31, 0.92);
      border-radius: 12px;
    }
    .spinner {
      width: 32px; height: 32px;
      border: 3px solid rgba(255,255,255,0.15);
      border-top-color: #FFA000;
      border-radius: 50%;
      animation: spin 0.9s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* Power bar — visible only while the user is holding the ball.
       Sweet spot band (80%-95%) is highlighted gold. Fill colour shifts
       through three zones to telegraph when to release. Stacked layout:
       a "POWER" label sits above the track. */
    .power-bar {
      position: absolute;
      right: 18px;
      top: 50%;
      transform: translateY(-50%);
      width: 60px;
      pointer-events: none;
      z-index: 5;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      animation: barIn 0.16s ease-out;
    }
    @keyframes barIn {
      from { opacity: 0; transform: translateY(-50%) scale(0.92); }
      to   { opacity: 1; transform: translateY(-50%) scale(1); }
    }
    .bar-label {
      color: #FFD54A;
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.7),
                   0 0 10px rgba(255, 215, 74, 0.5);
      line-height: 1;
    }
    .bar-track {
      position: relative;
      width: 30px;
      height: 230px;
      background: rgba(8, 4, 24, 0.65);
      border: 1.5px solid rgba(255, 255, 255, 0.22);
      border-radius: 16px;
      overflow: hidden;
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
    }
    .bar-sweet {
      position: absolute;
      left: 0; right: 0;
      bottom: 80%;
      height: 15%;
      background: rgba(255, 215, 0, 0.18);
      border-top: 1.5px solid rgba(255, 215, 0, 0.9);
      border-bottom: 1.5px solid rgba(255, 215, 0, 0.9);
      pointer-events: none;
    }
    .bar-fill {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      transition: background-color 0.12s, box-shadow 0.12s;
    }
    .bar-fill.zone-low {
      background: linear-gradient(to top, #FFA000, #FFD54F);
    }
    .bar-fill.zone-sweet {
      background: linear-gradient(to top, #43A047, #C6FF6E);
      box-shadow: 0 0 14px rgba(198, 255, 110, 0.7);
    }
    .bar-fill.zone-over {
      background: linear-gradient(to top, #B71C1C, #F44336);
    }
  `]
})
export class BowlingSceneComponent implements AfterViewInit, OnDestroy {
  /** Reference to the `<canvas>` element where Three.js renders.
   *  Resolved synchronously (`static: true`) so it's available in
   *  `ngAfterViewInit` for renderer construction. */
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  // ──────────────────────────────────────────────────────────────────
  // Inputs from the host (BowlingGameComponent)
  // ──────────────────────────────────────────────────────────────────

  /** Active language for in-scene text (loading spinner, swipe/tap
   *  hint copy, power-bar label). 'ar' or 'en'. */
  @Input() currLang: any = 'en';
  /** 10-bool mask: which pins should stand at the next rack-up. The
   *  scene reads this on every `rackTick` change inside the main
   *  loop (`restorePinsFromMask`). Always all-true in our simplified
   *  one-roll-per-frame model. */
  @Input() pinsStanding: boolean[] = Array(10).fill(true);
  /** Increment-to-trigger tick from the host. Each bump → re-rack
   *  pins, reset ball, return camera to idle. The scene tracks
   *  `lastRackTick` to detect the change inside the main loop. */
  @Input() rackTick = 0;
  /** Blocks pointer-down on the canvas while the host is showing
   *  banners / next-up cards. Read at the top of `onPointerDown`
   *  (early-return if true). Doesn't affect in-flight rolls. */
  @Input() inputDisabled = false;

  /** Cached scoreboard data — backing field for the setter below. */
  private _scoreboardData: Array<{ name: string; score: number; color: string }> | null = null;
  /**
   * Live scoreboard data drawn into the on-lane CRT screen above the
   * pin deck. Setter triggers an **immediate** redraw of the panel
   * canvas (via `redrawScorePanel`) so the rendered score updates
   * the same frame the parent's `BowlingState.totalScore` changes.
   * The main render loop also redraws every ~120 ms for animated
   * bits (border pulse, LIVE blink, scanline scroll), but those are
   * cosmetic — the score number itself is data-driven via the
   * setter.
   */
  @Input() set scoreboardData(value: Array<{ name: string; score: number; color: string }> | null) {
    this._scoreboardData = value;
    if (this.loaded) this.redrawScorePanel();
  }

  // ──────────────────────────────────────────────────────────────────
  // Outputs to the host
  // ──────────────────────────────────────────────────────────────────

  /** Fires the moment `launch()` runs — host flips `rollInFlight`
   *  to true so `ngOnDestroy`-style guards know a roll is mid-air. */
  @Output() rollStarted = new EventEmitter<void>();
  /** Fires inside `beginHold()` (the moment settle is detected, not
   *  after the hold). Payload reports the count + indices of pins
   *  knocked on this roll — the host's `onRollComplete` consumes
   *  this to drive `recordBowlingRoll`, the banner, and the
   *  next-up card. */
  @Output() rollComplete = new EventEmitter<{ pinsKnocked: number; knockedIndices: number[] }>();
  /** Fires once after async assets load + scene constructs + first
   *  `startLoop()`. Host responds by bumping `rackTick` so the
   *  initial pin rack appears. */
  @Output() ready = new EventEmitter<void>();

  // ──────────────────────────────────────────────────────────────────
  // Public state — bound by the inline template (*ngIf, [class.x],
  // [style.height.%], etc.)
  // ──────────────────────────────────────────────────────────────────

  /** False until async load completes; the loading spinner overlay
   *  shows while this is false. */
  loaded = false;
  /** True from `launch()` until `beginHold()`. Bound by hint cues +
   *  power-bar visibility checks. */
  rolling = false;
  /** True from `beginHold()` until `finishHold()` — i.e. while the
   *  camera is sitting on the pin deck after settle. */
  holding = false;
  /** True after the player has finished step 1 (position locked) and is
      now waiting for step 2 (the hold-to-charge gesture). Resets on every
      new rack so the next bowler starts in the positioning phase. */
  positioned = false;
  /** Hold-and-release power-bar state. While true, the bar is visible and
      filling. Bound from the template — read-only outside this component. */
  charging = false;
  /** 0..1 — current bar fill. Mapped to the .bar-fill height in the template. */
  chargeLevel = 0;
  /** Whether the on-lane swipe / tap hints are currently visible.
   *  Auto-set on init from localStorage (`bowling.hintsDismissed`) so a
   *  returning user doesn't see them every session. Flipped to false the
   *  first time the ball touches a pin (and the dismissal persists). The
   *  parent flips it back to true via `showHints()` when the player
   *  closes the How-to-Play modal. */
  hintsActive = false;
  /** localStorage key that, when present, suppresses auto-show of
   *  the in-scene hints on subsequent visits. Set on first pin
   *  contact, cleared by `showHints()`. */
  private static readonly HINTS_STORAGE_KEY = 'bowling.hintsDismissed';

  // ──────────────────────────────────────────────────────────────────
  // Lazy-loaded three.js + cannon-es module references — populated in
  // ngAfterViewInit by `BowlingEngineService.load()`. Typed as the
  // module shape via `typeof import(...)` so TS still gives us
  // autocomplete without bundling Three at module-scope.
  // ──────────────────────────────────────────────────────────────────
  private THREE!: typeof import('three');
  private CANNON!: typeof import('cannon-es');

  // ──────────────────────────────────────────────────────────────────
  // Three.js core objects (`any` to avoid pulling Three's types into
  // module-scope; constructed inside `initThree()`).
  // ──────────────────────────────────────────────────────────────────

  /** `WebGLRenderer` — disposed in `disposeScene` on teardown. */
  private renderer: any = null;
  /** Root `Scene` object — all meshes are added to this. */
  private scene: any = null;
  /** Single `PerspectiveCamera` — three-state-machine'd via `camState`. */
  private camera: any = null;
  /** Cannon `World` — gravity, broadphase, contact materials,
   *  bodies. Stepped by `world.step(1/60, dt, 3)` each frame. */
  private world: any = null;

  /** The single bowling ball — Three.js mesh + cannon body kept in
   *  lockstep by `syncMeshesFromBodies` each frame. */
  private ball!: { mesh: any; body: any };
  /** Active 10-pin rack — populated by `buildPins`. */
  private pins: Pin[] = [];
  /** Yellow target-arrow meshes painted on the lane (3-6-9 visual
   *  aids for the player). Populated by `buildTargetArrows`. */
  private targetArrows: any[] = [];

  /** Active `requestAnimationFrame` handle for the main render loop.
   *  Cancelled in `ngOnDestroy`. */
  private rafId: number | null = null;
  /** Counts consecutive frames where settle conditions were true.
   *  Once it crosses the threshold (10 frames ≈ 167 ms at 60 fps),
   *  `checkSettle` calls `beginHold`. Reset to 0 every time the ball
   *  or any pin is still moving. */
  private settleStableFrames = 0;
  /** `performance.now()` timestamp after which the roll is force-
   *  ended even if pins / ball haven't settled — failsafe against a
   *  ball stuck in the pit forever. Reset to `+8000ms` on `launch`. */
  private rollDeadline = 0;
  /** Flips to true the first time the ball touches a pin in the
   *  current roll, so the pin-hit SFX plays once (not 10 times for
   *  each pin in the chain). Reset on every `launch()`. */
  private pinContactPlayed = false;
  /** Handle to the looping ball-roll SFX so we can stop it the
   *  moment the ball hits a pin (or the roll settles, for gutter
   *  balls). Null when no roll is in flight. */
  private rollingAudio: HTMLAudioElement | null = null;

  // ── Live score-panel CRT screen above the main pin deck ──────────
  // The on-lane scoreboard rendered as a CanvasTexture. The canvas is
  // owned by this component (created in `buildLiveScorePanelMesh`),
  // painted by `redrawScorePanel`, and uploaded to the GPU via
  // `tex.needsUpdate = true`.

  /** Off-screen `<canvas>` element holding the score-panel pixels. */
  private scorePanelCanvas: HTMLCanvasElement | null = null;
  /** 2D context — kept around so each redraw doesn't re-fetch it. */
  private scorePanelCtx: CanvasRenderingContext2D | null = null;
  /** `THREE.CanvasTexture` wrapping the canvas. Shared with the
   *  score-panel mesh's material as `map`. */
  private scorePanelTex: any = null;
  /** Last `performance.now()` we redrew the score panel — gates the
   *  ~120 ms throttle in the main loop. */
  private lastScorePanelRedraw = 0;

  // ── Ghost-lane "screen saver" CRT screens ────────────────────────
  // One canvas / texture is shared across all four ghost-lane meshes
  // so a single redraw pushes pixels to all four screens (they
  // animate in sync, like a row of commercial bowling-alley
  // monitors). Painted by `redrawGhostScreen` on a ~150 ms throttle.

  /** Off-screen canvas for the shared ghost-lane animation. */
  private ghostScreenCanvas: HTMLCanvasElement | null = null;
  /** 2D context — see `scorePanelCtx`. */
  private ghostScreenCtx: CanvasRenderingContext2D | null = null;
  /** Shared `THREE.CanvasTexture` — referenced by the material on
   *  every ghost-lane score-panel mesh. */
  private ghostScreenTex: any = null;
  /** Throttle gate for `redrawGhostScreen`. */
  private lastGhostScreenRedraw = 0;
  /** Timestamp at which `finishHold()` should fire — set in
   *  `beginHold()` to `now + HOLD_AFTER_SETTLE_MS`. Polled by the
   *  main loop. */
  private holdUntil = 0;
  /** Last main-loop frame timestamp — diff vs `now` is the `dt`
   *  passed to `world.step`. */
  private lastFrameTime = 0;
  /** Set in `ngOnDestroy` so async callbacks (GLB load, charge tick)
   *  can early-return rather than touch a torn-down scene. */
  private disposed = false;
  /** Last-seen rackTick — main loop compares against the current
   *  `@Input() rackTick` and triggers a re-rack on change. Starts
   *  at -1 so the first onSceneReady→rackTick++ (which sends 1)
   *  registers as a change. */
  private lastRackTick = -1;

  // ── Drag/aim/launch state ────────────────────────────────────────

  /** True while the player has the pointer down on the canvas. Both
   *  positioning (step 1) and charging (step 2) gestures set this. */
  private dragging = false;
  /** PointerId of the captured pointer — used to filter out other
   *  pointers (multi-touch, mouse-on-touch screen) so a second
   *  finger can't disrupt an in-flight gesture. */
  private dragPointerId: number | null = null;
  /** Current ball X target while dragging — clamped to the lane half
   *  width minus ball radius so the ball can't drift into a gutter
   *  before launch. Smoothly lerped onto the body's actual X each
   *  frame. */
  private aimX = 0;
  /** Camera state — see the `CamState` type at the top of the file. */
  private camState: CamState = 'idle';

  // ── Hold-charge (power-bar) state ────────────────────────────────
  // The bar fills 0% → 100% over BAR_FILL_MS while the pointer is
  // held. Release fires the shot at the current charge level; hitting
  // 100% auto-fires as a foul (overcharge).

  /** `performance.now()` of the pointer-down that started charging. */
  private chargeStartTime = 0;
  /** rAF handle for the charge-bar tick (separate from the main
   *  render loop — runs inside NgZone so the `chargeLevel` writes
   *  trigger CD for the bound `[style.height.%]`). */
  private chargeRafId: number | null = null;

  constructor(
    /** Lazy GLB / Three / Cannon loader (singleton). */
    private engine: BowlingEngineService,
    /** Host element — used to read `clientWidth` / `clientHeight` for
     *  camera aspect + renderer size on init and resize. */
    private host: ElementRef<HTMLElement>,
    /** Ngzone — the main render loop runs `runOutsideAngular` so per-
     *  frame physics doesn't fire CD; explicit `ngZone.run` wraps
     *  any field write that needs to flow into Angular bindings. */
    private ngZone: NgZone,
    /** Used to play / stop ball-roll loop and pin-hit one-shot SFX. */
    private gameAudioService: GameAudioService
  ) {}

  /**
   * Async init pipeline — runs after Angular has rendered the
   * `<canvas>` element.
   *
   * Order matters:
   *   1. Resolve `three` + `cannon-es` modules (lazy import).
   *   2. Load pin GLB template (graceful fallback to procedural
   *      LatheGeometry pin if it fails).
   *   3. Load ball GLB template (graceful fallback to procedural
   *      sphere material if it fails).
   *   4. Build Three.js renderer / scene / camera / lights.
   *   5. Build cannon physics world.
   *   6. Build the lane geometry + alley environment + pins + ball.
   *   7. Attach pointer event listeners.
   *   8. Read the `hintsActive` localStorage flag.
   *   9. Set `loaded = true`, emit `ready`, start the render loop.
   *
   * Each await is followed by a `if (this.disposed) return;` guard
   * so the user can navigate away mid-load without leaving the
   * scene partially constructed.
   */
  async ngAfterViewInit(): Promise<void> {
    const { THREE, CANNON } = await this.engine.load();
    if (this.disposed) return;
    this.THREE = THREE;
    this.CANNON = CANNON;

    // Try to load the GLB pin model in parallel with scene init. If it fails
    // (404, parse error, etc.) we silently fall back to the procedural
    // LatheGeometry pin so the game still works.
    let pinTemplate: any = null;
    try {
      pinTemplate = await this.engine.loadPinModel();
    } catch (err) {
      console.warn('[bowling] pin model failed to load — using procedural pin', err);
    }
    // Same dance for the ball model — graceful fallback to a procedural
    // sphere if the GLB asset fails to load.
    let ballTemplate: any = null;
    try {
      ballTemplate = await this.engine.loadBallModel();
    } catch (err) {
      console.warn('[bowling] ball model failed to load — using procedural sphere', err);
    }
    if (this.disposed) return;

    this.initThree();
    this.initPhysics();
    this.buildLane(pinTemplate);
    this.buildPins(pinTemplate);
    this.buildBall(ballTemplate);
    this.attachPointerHandlers();
    // Show on-lane hints only on first visit. The dismissal flag is
    // set by the collide listener after the first pin hit; cleared
    // again by `showHints()` when the parent's How-to-Play modal
    // closes.
    try {
      this.hintsActive = !localStorage.getItem(BowlingSceneComponent.HINTS_STORAGE_KEY);
    } catch { this.hintsActive = true; }
    this.loaded = true;
    this.ready.emit();
    this.startLoop();
  }

  /**
   * Re-enable the on-lane swipe / tap hints. Called by the parent
   * when the player closes the How-to-Play modal — they've just
   * watched the explainer, so showing the in-scene cues reinforces
   * the same gestures on the actual ball.
   */
  showHints(): void {
    this.hintsActive = true;
    try { localStorage.removeItem(BowlingSceneComponent.HINTS_STORAGE_KEY); } catch { /* ignore */ }
  }

  /**
   * Teardown — cancels both the main render rAF and the charge-tick
   * rAF, removes pointer handlers, stops the looping ball-roll SFX
   * (so audio doesn't keep playing if the user navigates away
   * mid-roll), and finally disposes the renderer + nulls scene refs
   * via `disposeScene`.
   *
   * Important: `disposeScene` does NOT iterate the scene tree
   * disposing geometries/materials — those are shared with cached
   * GLB templates in `BowlingEngineService`, and disposing them
   * caused blank-scene-on-reopen bugs (see changelog 2026-05-07).
   */
  ngOnDestroy(): void {
    this.disposed = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.stopChargeTick();
    this.detachPointerHandlers();
    // Cut any lingering ball-roll SFX so it doesn't keep looping after
    // the user leaves the game mid-roll.
    if (this.rollingAudio) {
      this.gameAudioService.stop(this.rollingAudio);
      this.rollingAudio = null;
    }
    this.disposeScene();
  }

  /**
   * Window-resize handler — keeps the renderer + camera in sync with
   * the host element's new client size. `setSize(w, h, false)` skips
   * setting the canvas's CSS dimensions (leaves it 100%/100% per
   * the inline styles). No-op if renderer/camera haven't been
   * constructed yet.
   */
  @HostListener('window:resize')
  onResize(): void {
    if (!this.renderer || !this.camera) return;
    const el = this.host.nativeElement;
    const w = el.clientWidth || 1;
    const h = el.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // ──────────────────────────────────────────────────────────
  // Scene + physics setup
  // ──────────────────────────────────────────────────────────

  /**
   * Build the Three.js renderer + scene + camera + every light.
   *
   * Light setup is intentionally layered:
   *   - **Hemisphere** (sky purple, ground warm) — base ambient bounce.
   *   - **Ambient white** — guarantees nothing goes pitch-black.
   *   - **Pin spotlight** — overhead "stage" light on the pin deck
   *     (the only shadow-caster — pins shadow each other and the
   *     ball shadow tracks down the lane).
   *   - **Approach spotlight** — warm pool over the bowler position.
   *   - **Wall-wash points** — six soft purples, three per side,
   *     paint the side walls.
   *   - **Gutter accents** — six blues, three per side, the
   *     "signature neon glow" that bleeds onto the lane edges.
   *   - **Back-light** — purple point behind the pins, paints the
   *     back wall + headpin halo. Range tightened to 3 m so it
   *     doesn't bleed forward through the wall onto the lane (see
   *     changelog 2026-05-07).
   *
   * Renderer config:
   *   - Anti-aliased + sRGB output + ACES filmic tone mapping for
   *     PBR-correct rendering of the GLB assets.
   *   - Pixel ratio capped at 1.75 to avoid the 2x+ retina cost.
   *   - PCF soft shadow map.
   *
   * Camera: telephoto FOV (38°) so the 18 m lane and far pins
   * don't shrink to specks. Initial pose set by `setIdleCamera`.
   */
  private initThree(): void {
    const THREE = this.THREE;
    const el = this.host.nativeElement;

    this.scene = new THREE.Scene();
    // Deep purple background + fog. Background and fog colours match so the
    // alley walls fade into the distance instead of clipping against a
    // contrasting backdrop. Fog range widened from the first pass so distant
    // walls and ghost lanes stay visible rather than disappearing into black.
    this.scene.background = new THREE.Color(0x16093a);
    this.scene.fog = new THREE.Fog(0x16093a, 14, 36);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvasRef.nativeElement,
      antialias: true
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.setSize(el.clientWidth || 1, el.clientHeight || 1, false);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    // Narrow FOV ("telephoto") so the 18 m lane and the pins at the far end
    // don't shrink to specks. The camera sits low and just behind the ball
    // for an over-the-shoulder framing.
    this.camera = new THREE.PerspectiveCamera(
      38,
      (el.clientWidth || 1) / (el.clientHeight || 1),
      0.05,
      80
    );
    this.setIdleCamera();

    // Soft purple ambient + warm ground bounce. Bumped intensity from the
    // first lighting pass so the walls / floor / ceiling actually read; the
    // mood comes from coloured spotlights, not from underexposing everything.
    const hemi = new THREE.HemisphereLight(0xc7b0ff, 0x2a1840, 1.1);
    this.scene.add(hemi);

    // Plain ambient — guarantees no surface ever goes pitch black even when
    // outside every spot/point light cone. Keeps the back walls visible.
    const ambient = new THREE.AmbientLight(0xffffff, 0.25);
    this.scene.add(ambient);

    // Overhead spotlight on the pin deck — the "stage light" that picks the
    // pins out and casts the ball's shadow down the lane.
    const pinSpot = new THREE.SpotLight(0xfff4d8, 2.4, 24, Math.PI / 3.0, 0.45, 1.2);
    pinSpot.position.set(0, 5.5, PIN_HEAD_Z + 1.5);
    pinSpot.target.position.set(0, 0, PIN_HEAD_Z - 0.2);
    pinSpot.castShadow = true;
    pinSpot.shadow.mapSize.set(1024, 1024);
    pinSpot.shadow.camera.near = 0.5;
    pinSpot.shadow.camera.far = 18;
    pinSpot.shadow.bias = -0.0004;
    this.scene.add(pinSpot);
    this.scene.add(pinSpot.target);

    // Approach spotlight — warm light over the bowler so the ball is clearly
    // lit, with its own halo on the lane near the foul line.
    const approachSpot = new THREE.SpotLight(0xffe2bd, 1.6, 12, Math.PI / 3.5, 0.55, 1.4);
    approachSpot.position.set(0, 3.6, BALL_START_Z + 0.5);
    approachSpot.target.position.set(0, 0, BALL_START_Z - 1.0);
    this.scene.add(approachSpot);
    this.scene.add(approachSpot.target);

    // Wall-wash lights — point lights placed near each side wall so the
    // walls + ghost lanes + ceiling are visibly lit instead of silhouetted.
    // Without these, the alley environment sinks into the fog.
    const wallWashColor = 0x9466ff;
    const wallWashZs = [BALL_START_Z, 2, PIN_HEAD_Z + 1];
    for (const side of [-1, 1]) {
      for (const z of wallWashZs) {
        const wash = new THREE.PointLight(wallWashColor, 0.9, 9, 1.4);
        wash.position.set(side * 3.2, 2.5, z);
        this.scene.add(wash);
      }
    }

    // Blue accent point lights along the gutter rails — the signature
    // neon glow that bleeds onto the lane edges. Switched from magenta
    // (0xff2eaa) to blue to harmonise with the blue side-wall banner.
    const accentColor = 0x3a7eff;
    const accentZs = [BALL_START_Z - 2, -2, PIN_HEAD_Z + 1.5];
    for (const side of [-1, 1]) {
      for (const z of accentZs) {
        const accent = new THREE.PointLight(accentColor, 0.9, 6, 1.5);
        accent.position.set(side * (LANE_WIDTH / 2 + GUTTER_WIDTH + 0.05), 0.4, z);
        this.scene.add(accent);
      }
    }

    // Purple backlight behind the pins (paints the back wall + headpin halo).
    const backLight = new THREE.PointLight(0x9e6aff, 1.4, 12, 1.3);
    backLight.position.set(0, 1.6, -LANE_LENGTH / 2 - 0.4);
    this.scene.add(backLight);
  }

  /**
   * Build the cannon-es physics world. Configuration:
   *
   *   - **Gravity**: standard Earth gravity (-9.82 m/s² along Y).
   *   - **Broadphase**: SAP (Sweep-And-Prune) — better than the default
   *     Naive broadphase for our scene with ~10 dynamic bodies +
   *     several statics; SAP exploits spatial coherence between
   *     frames.
   *   - **`allowSleep: true`** — bodies that sit still long enough
   *     get put to sleep so they're skipped from broadphase. The ball
   *     specifically does NOT have allowSleep on its body (set in
   *     `buildBall`) because Cannon ignores `velocity.set` calls on
   *     sleeping bodies — that would silently swallow the launch.
   *   - **Contact friction 0.10** — moderate; lets the ball spin
   *     naturally as it rolls but not overly grippy.
   *   - **Contact restitution 0.28** — pins bounce off each other and
   *     the ball with a realistic-looking damped bounce.
   */
  private initPhysics(): void {
    const CANNON = this.CANNON;
    this.world = new CANNON.World({ gravity: new CANNON.Vec3(0, -9.82, 0) });
    this.world.broadphase = new CANNON.SAPBroadphase(this.world);
    this.world.allowSleep = true;
    this.world.defaultContactMaterial.friction = 0.10;
    this.world.defaultContactMaterial.restitution = 0.28;
  }

  /**
   * Build the playing surface + everything around it: the alley
   * environment first (walls/floor/ceiling/far-end wall — built by
   * delegating to `buildAlleyEnvironment`), then the lane wood
   * itself, the gutters, the pit, the pinsetter back wall, and the
   * score-panel mesh that sits on it. Also adds the static cannon
   * physics bodies for the lane top + pit floor + side walls + back
   * wall.
   *
   * @param pinTemplate Optional GLB pin Mesh — passed through to the
   *   alley environment so ghost lanes reuse the same pin model as
   *   the main lane (no second load, no second normalisation).
   */
  private buildLane(pinTemplate: any | null = null): void {
    const THREE = this.THREE;
    const CANNON = this.CANNON;

    // Build the surrounding bowling-alley environment first so the lane reads
    // as part of an enclosed space rather than floating in a void. The GLB
    // pin template threads through so ghost-lane pins use the same model as
    // the main lane's interactive pins.
    this.buildAlleyEnvironment(pinTemplate);

    // Lane (the playing surface — exactly 18.29 m). Glossier than before so
    // the overhead spotlight reads as a highlight down the centre of the
    // lane (matches the "polished bowling alley" vibe of reference games).
    const laneTex = this.buildWoodTexture();
    const laneMat = new THREE.MeshStandardMaterial({
      map: laneTex,
      roughness: 0.14,
      metalness: 0.18
    });
    const lane = new THREE.Mesh(
      new THREE.BoxGeometry(LANE_WIDTH, 0.12, LANE_LENGTH),
      laneMat
    );
    lane.position.set(0, -0.06, 0);
    lane.receiveShadow = true;
    this.scene.add(lane);

    // (Approach plank + foul-line stripe removed — the ball now sits
    // directly on the lane wood, 1.5 m inside the front edge, instead of
    // on a separate "dock" behind the foul line.)

    // ── Lane physics: a finite Box, top at y=0 ──────────────────────────
    // Replaces the infinite Plane that used to live here. Finite so pins
    // and the ball can fall off the back edge into a real bowling pit
    // (added below). Width covers lane + both gutters + a hair of buffer.
    const lanePhysHalfThickness = 0.05;
    const lanePhysHalfWidth = (LANE_WIDTH + GUTTER_WIDTH * 2 + 0.04) / 2;
    const lanePhysShape = new CANNON.Box(new CANNON.Vec3(
      lanePhysHalfWidth, lanePhysHalfThickness, LANE_LENGTH / 2
    ));
    const lanePhysBody = new CANNON.Body({
      type: CANNON.Body.STATIC, shape: lanePhysShape
    });
    lanePhysBody.position.set(0, -lanePhysHalfThickness, 0);  // top at y=0
    this.world.addBody(lanePhysBody);

    // ── Pit physics: lower Box behind the lane ──────────────────────────
    // Catches anything that rolls off the back edge so pins + ball land
    // in the pit instead of falling forever.
    const pitHalfLength = (PIT_FRONT_Z - PIT_BACK_Z) / 2;
    const pitPhysShape = new CANNON.Box(new CANNON.Vec3(
      lanePhysHalfWidth, lanePhysHalfThickness, pitHalfLength
    ));
    const pitPhysBody = new CANNON.Body({
      type: CANNON.Body.STATIC, shape: pitPhysShape
    });
    pitPhysBody.position.set(
      0,
      -PIT_DEPTH - lanePhysHalfThickness,         // top at y=-PIT_DEPTH
      (PIT_FRONT_Z + PIT_BACK_Z) / 2
    );
    this.world.addBody(pitPhysBody);

    // ── Pit visuals: dark sunken floor + vertical drop face ─────────────
    // Spans the FULL ALLEY WIDTH (not just the lane setup) so the entire
    // back-of-alley area reads as one continuous dark pit. This also
    // fills the side gaps that would otherwise be empty void after
    // shrinking the alley parquet floor.
    const pitVisualHalfWidth = ALLEY_HALF_WIDTH;
    const pitMat = new THREE.MeshStandardMaterial({
      color: 0x05030f, roughness: 0.95, metalness: 0.0
    });
    const pitFloor = new THREE.Mesh(
      new THREE.BoxGeometry(
        pitVisualHalfWidth * 2,
        lanePhysHalfThickness * 2,
        pitHalfLength * 2
      ),
      pitMat
    );
    pitFloor.position.set(
      0,
      -PIT_DEPTH - lanePhysHalfThickness,
      (PIT_FRONT_Z + PIT_BACK_Z) / 2
    );
    pitFloor.receiveShadow = true;
    this.scene.add(pitFloor);

    // Vertical step face — the visible drop where the lane ends. Full
    // alley width so it visually unifies with the wide pit floor.
    const pitStep = new THREE.Mesh(
      new THREE.BoxGeometry(pitVisualHalfWidth * 2, PIT_DEPTH, 0.04),
      pitMat
    );
    pitStep.position.set(0, -PIT_DEPTH / 2, PIT_FRONT_Z - 0.02);
    this.scene.add(pitStep);

    // Gutters (visual only) — slightly recessed below the lane for depth,
    // capped with a glowing blue neon strip on each inner edge.
    const gutterMat = new THREE.MeshStandardMaterial({
      color: 0x100a26, roughness: 0.85, metalness: 0.1
    });
    const gutterGeom = new THREE.BoxGeometry(GUTTER_WIDTH, 0.25, LANE_LENGTH);
    for (const side of [-1, 1]) {
      const g = new THREE.Mesh(gutterGeom, gutterMat);
      g.position.set(side * (LANE_WIDTH / 2 + GUTTER_WIDTH / 2), -0.12, 0);
      g.receiveShadow = true;
      this.scene.add(g);
    }

    // Blue neon strip running the length of each gutter inner edge — the
    // signature glow that makes the alley read as a "modern bowling lounge".
    // Was magenta (0xff2eaa); now blue to match the blue side-wall banner.
    const neonStripMat = new THREE.MeshBasicMaterial({ color: 0x3399ff });
    const neonStripGeom = new THREE.BoxGeometry(0.04, 0.04, LANE_LENGTH);
    for (const side of [-1, 1]) {
      const strip = new THREE.Mesh(neonStripGeom, neonStripMat);
      strip.position.set(side * (LANE_WIDTH / 2 + 0.02), 0.02, 0);
      this.scene.add(strip);
    }

    // Pinsetter / back wall — sized to ONE lane setup (lane + 2 gutters,
    // 2.2 m) so each lane has its own visible pinsetter rather than the
    // walls overlapping into one continuous black wall. The 0.4 m gaps
    // between adjacent back walls are filled by lane-divider pillars
    // built later in `buildAlleyEnvironment` so the eye reads three
    // separate lanes instead of one void. Wall sits at PIT_BACK_Z - 0.1,
    // starts at the pit floor (y=-PIT_DEPTH), rises 3 m.
    const pinsetterMat = new THREE.MeshStandardMaterial({
      color: 0x1c0e3c, roughness: 0.4, metalness: 0.35
    });
    const backWallZ = PIT_BACK_Z - 0.1;
    const backWallHeight = 3.0;
    const backWallCenterY = -PIT_DEPTH + backWallHeight / 2;
    const laneSetupWidth = LANE_WIDTH + GUTTER_WIDTH * 2;     // 2.2 m
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(laneSetupWidth, backWallHeight, 0.2),
      pinsetterMat
    );
    back.position.set(0, backWallCenterY, backWallZ);
    back.receiveShadow = true;
    this.scene.add(back);

    // (Magenta horizontal accent strip above the pin deck removed —
    // it clashed with the new blue palette and the score-panel screen
    // below already provides the only "glow above the pin deck" cue
    // we need.)

    // Score panel — animated CRT-style screen above the pin deck. Drawn
    // into a CanvasTexture so we can paint live scores + animated
    // accents (border pulse, LIVE indicator, scanline). Built and
    // attached here; live scoreboard data is fed via the
    // `scoreboardData` setter and the throttled redraw in the main
    // loop drives the animated bits.
    //
    // Sized for legibility from the bowler's POV — the back wall is
    // ~21 m from the idle camera, so a 1.7×0.55 m panel reads as a
    // postage stamp. 2.0×0.9 m is roughly 4× the area and fits inside
    // both the main back wall (2.8 m wide) and the ghost back walls
    // (2.2 m wide, used by the ghost-lane screen savers).
    const scorePanelGeom = new THREE.BoxGeometry(2.0, 0.9, 0.04);
    const scorePanel = this.buildLiveScorePanelMesh(scorePanelGeom);
    scorePanel.position.set(0, 2.0, backWallZ + 0.11);
    this.scene.add(scorePanel);

    const backWallShape = new CANNON.Box(new CANNON.Vec3(
      (LANE_WIDTH + 1.2) / 2, backWallHeight / 2, 0.1
    ));
    const backBody = new CANNON.Body({ type: CANNON.Body.STATIC, shape: backWallShape });
    backBody.position.set(0, backWallCenterY, backWallZ);
    this.world.addBody(backBody);

    // Side walls keep the ball (and pin debris) on the lane area. They must:
    //   1. be tall enough that a ball bouncing off pin impact can't clear them,
    //   2. cover the entire ball path including the approach where the ball
    //      starts (z = +BALL_START_Z) and slightly past the pin deck.
    // Without (1) the ball can launch upward off pins and clear a short wall;
    // without (2) a sideways nudge near the foul line would let the ball roll
    // off into the void on the infinite ground plane.
    const wallZStart = PIT_BACK_Z - 0.2;                // past the pit's back end
    const wallZEnd = BALL_START_Z + 1.5;                // past the ball start
    const wallHalfZ = (wallZEnd - wallZStart) / 2;
    const wallCenterZ = (wallZEnd + wallZStart) / 2;
    const wallHalfHeight = 1.2;                          // 2.4 m total — un-clearable
    const wallX = LANE_WIDTH / 2 + GUTTER_WIDTH + 0.02;  // outer edge of gutter
    const sideWallShape = new CANNON.Box(new CANNON.Vec3(0.05, wallHalfHeight, wallHalfZ));
    for (const side of [-1, 1]) {
      const body = new CANNON.Body({ type: CANNON.Body.STATIC, shape: sideWallShape });
      body.position.set(side * wallX, wallHalfHeight, wallCenterZ);
      this.world.addBody(body);
    }

    // 3-6-9 target arrows (the seven dovetail darts a real lane has at ~4.5 m).
    this.buildTargetArrows();
  }

  /**
   * Builds the bowling-alley shell around the lane: dark floor extending out
   * to the side walls, side walls flanking the lane, low ceiling, and an
   * adjacent ghost lane on each side faded into the fog. Together these turn
   * the scene from "lane in a void" into "you're standing in a lounge-style
   * bowling alley". All meshes are visual only — physics walls are still
   * the tight set right at the gutter edge built in `buildLane`.
   */
  private buildAlleyEnvironment(pinTemplate: any | null = null): void {
    const THREE = this.THREE;
    // Alley shell dimensions (use module-level constants so buildLane can
    // reach them too — pit visuals span the full alley width).
    const alleyHalfWidth = ALLEY_HALF_WIDTH;
    const alleyHeight = ALLEY_HEIGHT;
    const alleyLength = LANE_LENGTH + APPROACH_LENGTH + 2.0;
    const alleyCenterZ = (BALL_START_Z + APPROACH_LENGTH / 2 + (-LANE_LENGTH / 2 - 0.6)) / 2;

    // Surrounding floor (outside the lane) — hungarian-point parquet PBR set
    // (Unity-format, CC0/CC-BY) at assets/game-mode/bowling/room/floor/.
    // Three maps — albedo, normal (OGL convention), AO. Tile factor sized so
    // each parquet block reads at roughly 1 m, dense enough to feel like a
    // real wood floor without obvious repetition.
    // Floor stops at the lane back edge (PIT_FRONT_Z) so it doesn't extend
    // *over* the pit area — that was the bug where the parquet was
    // visible above the pit floor, hiding the dark sunken pit entirely.
    const floorFrontZ = alleyCenterZ + alleyLength / 2;
    const floorBackZ = PIT_FRONT_Z;
    const floorLength = floorFrontZ - floorBackZ;
    const floorCenterZ = (floorFrontZ + floorBackZ) / 2;
    const floorTexLoader = new THREE.TextureLoader();
    const floorAlbedo = floorTexLoader.load('assets/game-mode/bowling/room/floor/floor_albedo.png');
    const floorNormal = floorTexLoader.load('assets/game-mode/bowling/room/floor/floor_normal.png');
    const floorAO     = floorTexLoader.load('assets/game-mode/bowling/room/floor/floor_ao.png');
    for (const tex of [floorAlbedo, floorNormal, floorAO]) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(alleyHalfWidth * 2 / 3, floorLength / 3); // ~3 m × 3 m tiles
      tex.anisotropy = 8;                                       // viewed at grazing angles
    }
    floorAlbedo.colorSpace = THREE.SRGBColorSpace;

    const floorMat = new THREE.MeshStandardMaterial({
      map: floorAlbedo,
      normalMap: floorNormal,
      aoMap: floorAO,
      roughness: 0.6,
      metalness: 0.05
    });
    const floorGeom = new THREE.PlaneGeometry(alleyHalfWidth * 2, floorLength);
    if (floorGeom.attributes.uv && !floorGeom.attributes.uv2) {
      floorGeom.setAttribute('uv2', floorGeom.attributes.uv);
    }
    const floor = new THREE.Mesh(floorGeom, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -0.07, floorCenterZ);
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Side walls — hand-drawn bowling-game banner (5778×2000, aspect ~2.9:1).
    // This is a *banner* not a seamless pattern, so the repeat is sized to
    // preserve the banner's natural aspect: shown 2× across the wall length
    // and 1× tall (banner height fills the wall height) → no distortion,
    // visible seam between the two repeats but the artwork joins itself.
    const texLoader = new THREE.TextureLoader();
    // Use the **content-cropped** version of the bowling banner JPG —
    // the original has a darker-blue border on all four sides (top,
    // bottom, left, right) which produced visible dark seams + strips
    // on the wall under any UV mapping. The cropped JPG strips the
    // padding once at the asset level (sips, ~3.85:1 aspect), so the
    // runtime UV is simple repeat-only.
    const wallAlbedo = texLoader.load('assets/game-mode/bowling/room/walls/wall_albedo_content.jpg');
    wallAlbedo.colorSpace = THREE.SRGBColorSpace;
    // Mirrored U wrap so the seam between the two horizontal copies
    // (at face U=0.5) is a smooth reflection of identical pixels rather
    // than the right edge of copy 1 abutting the left edge of copy 2.
    // V stays single-copy (repeat.y = 1) since the cropped texture is
    // already pure content top-to-bottom.
    wallAlbedo.wrapS = THREE.MirroredRepeatWrapping;
    wallAlbedo.wrapT = THREE.RepeatWrapping;
    wallAlbedo.repeat.set(2, 1);
    wallAlbedo.offset.set(0, 0);
    wallAlbedo.anisotropy = 4;

    const wallMat = new THREE.MeshStandardMaterial({
      map: wallAlbedo, roughness: 0.7, metalness: 0.05
    });
    const wallGeom = new THREE.BoxGeometry(0.3, alleyHeight, alleyLength);
    for (const side of [-1, 1]) {
      const w = new THREE.Mesh(wallGeom, wallMat);
      w.position.set(side * alleyHalfWidth, alleyHeight / 2 - 0.06, alleyCenterZ);
      w.receiveShadow = true;
      this.scene.add(w);
    }

    // Alley far-end wall — closes the back of the room behind the row of
    // three pinsetter pit walls. Painted with the hand-drawn bowling-
    // sticker doodle JPG as graffiti so the strip visible above the
    // pinsetter walls (and the side strips beside the outermost ghosts)
    // doesn't read as a black void. Tinted blue-purple so the white
    // sticker BG blends into the alley palette instead of glowing white
    // against everything else. Texture repeats (3, 1) — moderate
    // density, ~3.7 m per repeat, big stickers visible from the
    // bowler POV ~21 m away.
    const farWallTex = texLoader.load('assets/game-mode/bowling/room/back/doodles.jpg');
    farWallTex.colorSpace = THREE.SRGBColorSpace;
    farWallTex.wrapS = THREE.RepeatWrapping;
    farWallTex.wrapT = THREE.RepeatWrapping;
    farWallTex.repeat.set(3, 1);
    farWallTex.anisotropy = 4;
    const farWallMat = new THREE.MeshStandardMaterial({
      map: farWallTex,
      // Soft blue-purple tint multiplies onto the texture; white
      // sticker BG → alley-palette wash, coloured doodles → muted
      // on-theme versions.
      color: 0x9aa0d8,
      roughness: 0.7,
      metalness: 0.05
    });
    // Position: just behind the pinsetter back walls (PIT_BACK_Z - 0.1
    // is at -10.845; the alley far end sits at the back of the alley
    // shell). 0.3 m thick so it has visible depth, full alley width,
    // full alley height.
    const farWallZ = alleyCenterZ - alleyLength / 2 + 0.15;
    const farWall = new THREE.Mesh(
      new THREE.BoxGeometry(alleyHalfWidth * 2, alleyHeight, 0.3),
      farWallMat
    );
    farWall.position.set(0, alleyHeight / 2 - 0.06, farWallZ);
    farWall.receiveShadow = true;
    this.scene.add(farWall);

    // Ceiling — commercial-style tile grid with a recessed spotlight in
    // EVERY cell. Grid + spotlight count share the same `(COLS, ROWS)` so
    // each light sits exactly in a cell centre.
    const CEILING_COLS = 6;
    const CEILING_ROWS = 12;
    const ceilingTex = this.buildCeilingGridTexture();
    ceilingTex.wrapS = THREE.RepeatWrapping;
    ceilingTex.wrapT = THREE.RepeatWrapping;
    ceilingTex.repeat.set(CEILING_COLS, CEILING_ROWS);
    const ceilingMat = new THREE.MeshStandardMaterial({
      map: ceilingTex, roughness: 0.85, metalness: 0.0
    });
    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(alleyHalfWidth * 2, alleyLength),
      ceilingMat
    );
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.set(0, alleyHeight - 0.06, alleyCenterZ);
    this.scene.add(ceiling);

    this.buildCeilingSpotlights(
      alleyHalfWidth, alleyHeight, alleyLength, alleyCenterZ,
      CEILING_COLS, CEILING_ROWS
    );

    // (Vertical purple neon strips on the side walls were removed — they
    // clashed with the textured wall banner.)

    // Ghost lanes — full setup mirroring the main lane. Each ghost lane
    // gets its own wood plank, gutters, blue neon strips, pinsetter back
    // wall, glowing accent strip above the pin deck, and a 10-pin cluster
    // at the back. All visual; no physics — these aren't playable, they
    // just complete the multi-lane look so the player feels they're in a
    // bowling alley with adjacent lanes.
    const ghostLaneOffset = LANE_WIDTH + GUTTER_WIDTH * 2 + 0.4;  // 2.6 m

    // Materials — shared across all ghost lanes for batching.
    const ghostLaneMat = new THREE.MeshStandardMaterial({
      map: this.buildWoodTexture(),
      roughness: 0.25,
      metalness: 0.15
    });
    const ghostGutterMat = new THREE.MeshStandardMaterial({
      color: 0x100a26, roughness: 0.85, metalness: 0.1
    });
    const ghostNeonMat = new THREE.MeshBasicMaterial({ color: 0x3399ff });
    const ghostBackMat = new THREE.MeshStandardMaterial({
      color: 0x1c0e3c, roughness: 0.4, metalness: 0.35
    });
    // (Magenta horizontal accent strip removed for ghost lanes too —
    // matches the main-lane cleanup. ghostAccentMat / ghostAccentGeom
    // and the per-ghost-lane accent mesh are gone.)

    // Geometries — shared instances.
    const ghostLaneGeom   = new THREE.BoxGeometry(LANE_WIDTH, 0.10, LANE_LENGTH);
    const ghostGutterGeom = new THREE.BoxGeometry(GUTTER_WIDTH, 0.25, LANE_LENGTH);
    const ghostNeonGeom   = new THREE.BoxGeometry(0.04, 0.04, LANE_LENGTH);
    const ghostBackGeom   = new THREE.BoxGeometry(LANE_WIDTH + GUTTER_WIDTH * 2, 3, 0.2);
    // Ghost-lane score panels share a single CanvasTexture-backed
    // material so all four animate in sync from one redraw call.
    // Geometry matches the main lane (2.0×0.9 m) so the row of screens
    // reads as a uniform set.
    const ghostScoreMat   = this.getGhostScreenMaterial();
    const ghostScoreGeom  = new THREE.BoxGeometry(2.0, 0.9, 0.04);

    for (const side of [-1, 1]) {
      const xCentre = side * ghostLaneOffset;

      // Lane plank
      const lane = new THREE.Mesh(ghostLaneGeom, ghostLaneMat);
      lane.position.set(xCentre, -0.06, 0);
      lane.receiveShadow = true;
      this.scene.add(lane);

      // Two gutters flanking this ghost lane
      for (const gSide of [-1, 1]) {
        const gutter = new THREE.Mesh(ghostGutterGeom, ghostGutterMat);
        gutter.position.set(
          xCentre + gSide * (LANE_WIDTH / 2 + GUTTER_WIDTH / 2),
          -0.12,
          0
        );
        gutter.receiveShadow = true;
        this.scene.add(gutter);
      }

      // Neon strips along the inner edge of each gutter (both sides)
      for (const stripSide of [-1, 1]) {
        const strip = new THREE.Mesh(ghostNeonGeom, ghostNeonMat);
        strip.position.set(
          xCentre + stripSide * (LANE_WIDTH / 2 + 0.02),
          0.02,
          0
        );
        this.scene.add(strip);
      }

      // (Per-lane pit visuals removed — the main-lane pit floor + step
      // built in `buildLane` now span the full alley width and cover all
      // three lanes' pit areas continuously.)

      // Pinsetter back wall + glowing accent strip — moved BEHIND the pit
      // (same z as the main lane's back wall) so the pit is visible.
      const ghostBackWallZ = PIT_BACK_Z - 0.1;
      const ghostBackWallHeight = 3.0;
      const ghostBackCenterY = -PIT_DEPTH + ghostBackWallHeight / 2;
      const back = new THREE.Mesh(ghostBackGeom, ghostBackMat);
      back.position.set(xCentre, ghostBackCenterY, ghostBackWallZ);
      back.receiveShadow = true;
      this.scene.add(back);

      // Score panel — animated CRT "screen saver" (shared canvas/texture
      // across all four ghost lanes). Same geometry + y as the main
      // lane so the row of screens lines up.
      const scorePanel = new THREE.Mesh(ghostScoreGeom, ghostScoreMat);
      scorePanel.position.set(xCentre, 2.0, ghostBackWallZ + 0.11);
      this.scene.add(scorePanel);
    }

    // (Lane dividers between adjacent pinsetters removed — first the
    // chunky dark pillars, then the slim cyan neon strips that briefly
    // replaced them. The doodle far-end wall behind the row of three
    // pinsetters now fills the 0.4 m gaps with visible graffiti, which
    // separates the lanes naturally without any divider geometry. No
    // physics impact: the dividers were Three.Mesh-only, the ball is
    // contained by the side walls + pinsetter back-wall cannon bodies.)

    // Ghost pins — 10 per ghost lane × 2 lanes = 20 total, batched into a
    // single `InstancedMesh` so the whole lot is one draw call. Uses the
    // GLB pin model when available (matching the main lane), falling back
    // to the procedural lathe pin if the GLB hadn't loaded.
    this.buildGhostPins(ghostLaneOffset, pinTemplate);
  }

  /**
   * One InstancedMesh covering all ghost-lane pins. The geometry is the
   * engine-baked unit-height pin template; per-instance matrix composes a
   * uniform `PIN_HEIGHT` scale + a translation to the standard 10-pin
   * triangle position offset by each ghost lane's X centre.
   */
  private buildGhostPins(ghostLaneOffset: number, pinTemplate: any | null): void {
    const THREE = this.THREE;
    const ghostLanesPerSide = 1;
    const totalPins = ghostLanesPerSide * 2 * PIN_LAYOUT.length;

    let ghostGeom: any;
    let ghostMat: any;
    let perInstanceScale: number;
    if (pinTemplate) {
      ghostGeom = pinTemplate.geometry;
      ghostMat = pinTemplate.material;
      perInstanceScale = PIN_HEIGHT;
    } else {
      ghostGeom = this.buildPinGeometry();
      ghostMat = new THREE.MeshStandardMaterial({
        color: 0xfdfaf0,
        roughness: 0.4,
        metalness: 0.05,
        emissive: 0x1a1a26,
        emissiveIntensity: 0.15
      });
      perInstanceScale = 1;
    }

    const pins = new THREE.InstancedMesh(ghostGeom, ghostMat, totalPins);
    pins.castShadow = false;
    pins.receiveShadow = false;

    const m = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3(perInstanceScale, perInstanceScale, perInstanceScale);
    let i = 0;
    for (const side of [-1, 1]) {
      const xCentre = side * ghostLaneOffset;
      for (const [row, col] of PIN_LAYOUT) {
        const px = xCentre + col * PIN_COL_SPACING;
        const pz = PIN_HEAD_Z - row * PIN_ROW_SPACING;
        pos.set(px, PIN_HEIGHT / 2, pz);
        m.compose(pos, quat, scl);
        pins.setMatrixAt(i++, m);
      }
    }
    pins.instanceMatrix.needsUpdate = true;
    this.scene.add(pins);
  }

  /**
   * Procedural ceiling texture: light-purple base + bright grid lines drawn
   * along the top + left edges of one tile. When tiled with
   * `RepeatWrapping`, these single-edge lines stitch with the adjacent
   * tiles' edges to form a clean continuous grid (no double-thick lines
   * at boundaries). Caller controls the cell count via `repeat.set`.
   *
   * Brighter than the first pass — the previous version was too dark for
   * the grid to read against the dim ceiling.
   */
  private buildCeilingGridTexture(): any {
    const THREE = this.THREE;
    const SIZE = 256;
    const c = document.createElement('canvas');
    c.width = SIZE;
    c.height = SIZE;
    const ctx = c.getContext('2d')!;
    // Lifted base colour so the grid + spotlight bulbs read clearly.
    ctx.fillStyle = '#3a2a5c';
    ctx.fillRect(0, 0, SIZE, SIZE);
    // One line at the top, one at the left of each tile. Adjacent tiles
    // contribute their own top+left lines for the right and bottom edges,
    // forming a clean grid without doubling line thickness.
    ctx.strokeStyle = 'rgba(220, 220, 240, 0.65)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(SIZE, 0);    // top edge
    ctx.moveTo(0, 0); ctx.lineTo(0, SIZE);    // left edge
    ctx.stroke();
    return new THREE.CanvasTexture(c);
  }

  /**
   * Recessed-spotlight grid mounted on the ceiling — one bulb per grid
   * cell. Single `InstancedMesh` of `COLS × ROWS` emissive discs facing
   * downward, one draw call. Discs are positioned at cell centres so
   * each spotlight sits exactly inside a grid square.
   *
   * No actual light sources — the existing scene lighting handles
   * illumination; these are pure visual.
   */
  private buildCeilingSpotlights(
    halfWidth: number,
    height: number,
    length: number,
    centerZ: number,
    cols: number,
    rows: number
  ): void {
    const THREE = this.THREE;
    const total = cols * rows;

    // Disc geometry pre-rotated so its face points DOWN. CircleGeometry
    // defaults to the XY plane with normal at +Z; rotateX(+π/2) sends that
    // normal to -Y (down toward the player). The previous -π/2 had it
    // pointing UP at the ceiling, so the disc was edge-on / back-faced
    // from the camera below and invisible.
    const discGeom = new THREE.CircleGeometry(0.18, 24);
    discGeom.rotateX(Math.PI / 2);
    // MeshBasicMaterial = full-bright, ignores scene lighting.
    // DoubleSide as a safety so a wrong-direction rotation can't hide it.
    const discMat = new THREE.MeshBasicMaterial({
      color: 0xfff4d0, side: THREE.DoubleSide
    });

    const lights = new THREE.InstancedMesh(discGeom, discMat, total);

    // Cell dimensions on the ceiling.
    const fullWidth = halfWidth * 2;
    const cellW = fullWidth / cols;
    const cellL = length / rows;
    const xStart = -halfWidth + cellW / 2;
    const zStart = centerZ - length / 2 + cellL / 2;

    const m = new THREE.Matrix4();
    let i = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = xStart + col * cellW;
        const z = zStart + row * cellL;
        // 7 cm below ceiling so the disc sits proud of the ceiling plane.
        m.makeTranslation(x, height - 0.07, z);
        lights.setMatrixAt(i++, m);
      }
    }
    lights.instanceMatrix.needsUpdate = true;
    this.scene.add(lights);
  }

  /**
   * Paint the seven yellow target arrows on the lane — the visual
   * 3-6-9 aiming aids real bowlers use to line up their roll. Pure
   * decoration — no physics, no interactivity. The user's positioning
   * gesture lines up the ball laterally; the arrows give a reference
   * for "aim at the second arrow from the centre" etc.
   */
  private buildTargetArrows(): void {
    const THREE = this.THREE;
    // Arrows are painted ~4.5 m from the foul line, pointing toward the pins.
    const arrowZ = FOUL_LINE_Z - 4.5;
    // Boards: 3, 6, 9 (and centre) from the centre line on each side.
    const boardOffsets = [-9, -6, -3, 0, 3, 6, 9];
    const boardWidth = LANE_WIDTH / 39; // 39 boards across regulation lane
    const arrowMat = new THREE.MeshStandardMaterial({
      color: 0xffd54a,
      emissive: 0x332200,
      roughness: 0.55
    });
    const triShape = new THREE.Shape();
    triShape.moveTo(0, 0.18);
    triShape.lineTo(-0.05, -0.08);
    triShape.lineTo(0.05, -0.08);
    triShape.closePath();
    const arrowGeom = new THREE.ShapeGeometry(triShape);
    boardOffsets.forEach((b, i) => {
      const arrow = new THREE.Mesh(arrowGeom, arrowMat);
      arrow.rotation.x = -Math.PI / 2;
      // Splay them in a fan so the row reads as a target group, not flat dots.
      arrow.position.set(b * boardWidth, 0.002, arrowZ - Math.abs(b) * 0.05);
      this.scene.add(arrow);
      this.targetArrows.push(arrow);
    });
  }

  /**
   * Builds the main lane's score-panel mesh as a CanvasTexture-backed
   * box. The canvas is owned by this component and redrawn periodically
   * (`redrawScorePanel`) — the texture's `needsUpdate` flag pushes the
   * new pixels to the GPU. Position + rotation are set by the caller.
   */
  private buildLiveScorePanelMesh(geometry: any): any {
    const THREE = this.THREE;
    const canvas = document.createElement('canvas');
    // 1024×460 ≈ 2.22:1, matching the new 2.0×0.9 m panel.
    canvas.width = 1024;
    canvas.height = 460;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Fall back to a flat material if 2D context unavailable (very rare).
      return new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ color: 0x4a25aa }));
    }
    this.scorePanelCanvas = canvas;
    this.scorePanelCtx = ctx;

    const tex = new THREE.CanvasTexture(canvas);
    // Three uses sRGB for displayed colours — without this, the canvas
    // pixels render too dark on top of the dark back wall.
    if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    this.scorePanelTex = tex;

    const mat = new THREE.MeshBasicMaterial({ map: tex });
    const mesh = new THREE.Mesh(geometry, mat);

    this.redrawScorePanel();
    return mesh;
  }

  /**
   * Repaints the score-panel canvas: dark CRT background, glowing border
   * pulse, "STRIKE LANE" header with a flashing LIVE dot, two-team score
   * area, and a slow-moving scanline. Cheap (one 1024x320 canvas, no
   * gradients per pixel) — called from the main render loop on a
   * ~120 ms throttle so the animation reads as smooth without burning
   * CPU at 60 fps.
   */
  private redrawScorePanel(): void {
    const ctx = this.scorePanelCtx;
    const canvas = this.scorePanelCanvas;
    if (!ctx || !canvas) return;
    const w = canvas.width;
    const h = canvas.height;
    const t = performance.now() * 0.001;

    // Background — vertical gradient, deep blue→black.
    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#0a0824');
    bg.addColorStop(1, '#1a0d4a');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Glowing animated border — pulses with a subtle sine wave.
    const borderAlpha = 0.6 + 0.35 * Math.sin(t * 1.8);
    ctx.strokeStyle = `rgba(80, 160, 255, ${borderAlpha})`;
    ctx.lineWidth = 8;
    ctx.strokeRect(10, 10, w - 20, h - 20);
    // Inner thin border.
    ctx.strokeStyle = 'rgba(120, 200, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.strokeRect(26, 26, w - 52, h - 52);

    // Header — "STRIKE LANE" centered, with a pulsing LIVE indicator on
    // the right. All sizes bumped roughly 1.5× from the smaller panel
    // to stay readable from the bowler's POV ~21 m away.
    ctx.font = 'bold 56px "Courier New", monospace';
    ctx.fillStyle = '#FFD54A';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#FFD54A';
    ctx.shadowBlur = 26;
    ctx.fillText('★  STRIKE LANE  ★', w / 2, 80);
    ctx.shadowBlur = 0;

    const livePulse = 0.55 + 0.45 * Math.sin(t * 4);
    ctx.fillStyle = `rgba(255, 60, 60, ${livePulse})`;
    ctx.beginPath();
    ctx.arc(w - 110, 80, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(255, 200, 200, ${0.75 + 0.25 * livePulse})`;
    ctx.font = 'bold 32px "Courier New", monospace';
    ctx.textAlign = 'left';
    ctx.fillText('LIVE', w - 88, 80);

    // Centre divider between team halves.
    ctx.strokeStyle = 'rgba(120, 200, 255, 0.4)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(w / 2, 145);
    ctx.lineTo(w / 2, h - 40);
    ctx.stroke();

    // Two-team scoreboard. Falls back to neutral placeholders if the
    // parent hasn't pushed scoreboardData yet (e.g. first frame).
    const data = this._scoreboardData && this._scoreboardData.length >= 2
      ? this._scoreboardData
      : [
          { name: 'Player 1', score: 0, color: '#3F51B5' },
          { name: 'Player 2', score: 0, color: '#F44336' }
        ];
    const teams = data.slice(0, 2);
    teams.forEach((team, i) => {
      const cx = (i === 0 ? 0.25 : 0.75) * w;
      // Team name — uppercased, in their colour.
      ctx.fillStyle = team.color;
      ctx.font = 'bold 42px "Courier New", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = team.color;
      ctx.shadowBlur = 18;
      const nameDraw = team.name.length > 14 ? team.name.slice(0, 13) + '…' : team.name;
      ctx.fillText(nameDraw.toUpperCase(), cx, 200);

      // Score — large LED-style numerals with team-coloured glow.
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 170px "Courier New", monospace';
      ctx.shadowColor = team.color;
      ctx.shadowBlur = 40;
      ctx.fillText(String(team.score | 0), cx, 340);
      ctx.shadowBlur = 0;
    });

    // Scanline — a subtle moving horizontal band, low alpha so it
    // doesn't fight the text. CRT throwback.
    const scanY = (t * 110) % h;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(0, scanY, w, 8);

    if (this.scorePanelTex) this.scorePanelTex.needsUpdate = true;
  }

  /**
   * Lazily builds the shared `CanvasTexture` painted on every ghost-lane
   * score panel. The same texture instance is applied to all four
   * meshes, so a single `redrawGhostScreen()` call updates all four —
   * they animate in sync. Returns the (cached) `MeshBasicMaterial` ready
   * to wrap the panel geometry.
   */
  private getGhostScreenMaterial(): any {
    const THREE = this.THREE;
    if (this.ghostScreenTex) {
      return new THREE.MeshBasicMaterial({ map: this.ghostScreenTex });
    }
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 460;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Fallback: flat purple if the 2D context fails.
      return new THREE.MeshBasicMaterial({ color: 0x4a25aa });
    }
    this.ghostScreenCanvas = canvas;
    this.ghostScreenCtx = ctx;

    const tex = new THREE.CanvasTexture(canvas);
    if (THREE.SRGBColorSpace) tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    this.ghostScreenTex = tex;

    this.redrawGhostScreen();
    return new THREE.MeshBasicMaterial({ map: tex });
  }

  /**
   * Repaints the ghost-screen "screen saver" canvas. Animated bits:
   * scrolling diagonal wash, pulsing border, "STRIKE LANE" title
   * glow, a bowling-ball icon bouncing horizontally, and a blinking
   * "▶ PLAY ◀" footer. Throttled in the main render loop.
   */
  private redrawGhostScreen(): void {
    const ctx = this.ghostScreenCtx;
    const canvas = this.ghostScreenCanvas;
    if (!ctx || !canvas) return;
    const w = canvas.width;
    const h = canvas.height;
    const t = performance.now() * 0.001;

    // Solid base background.
    ctx.fillStyle = '#070420';
    ctx.fillRect(0, 0, w, h);

    // Scrolling diagonal gradient — the "screen saver" wash. Phase
    // shifts with time so the colour band drifts across the panel.
    const phase = t * 0.5;
    const gx0 = (Math.cos(phase) * 0.5 + 0.5) * w;
    const gy0 = 0;
    const gx1 = (Math.cos(phase + Math.PI) * 0.5 + 0.5) * w;
    const gy1 = h;
    const grad = ctx.createLinearGradient(gx0, gy0, gx1, gy1);
    grad.addColorStop(0, 'rgba(50, 100, 255, 0.45)');
    grad.addColorStop(0.5, 'rgba(120, 60, 220, 0.30)');
    grad.addColorStop(1, 'rgba(255, 80, 160, 0.45)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Outer + inner border (matches main panel for visual continuity).
    const borderAlpha = 0.55 + 0.35 * Math.sin(t * 1.5);
    ctx.strokeStyle = `rgba(80, 160, 255, ${borderAlpha})`;
    ctx.lineWidth = 8;
    ctx.strokeRect(10, 10, w - 20, h - 20);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.20)';
    ctx.lineWidth = 2;
    ctx.strokeRect(26, 26, w - 52, h - 52);

    // Title — "STRIKE LANE" with a pulsing glow.
    const titlePulse = 0.75 + 0.25 * Math.sin(t * 2);
    ctx.font = 'bold 92px "Courier New", monospace';
    ctx.fillStyle = `rgba(255, 215, 74, ${titlePulse})`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#FFD54A';
    ctx.shadowBlur = 35;
    ctx.fillText('STRIKE LANE', w / 2, h * 0.28);
    ctx.shadowBlur = 0;

    // Bouncing bowling-ball icon — slides horizontally and bounces
    // vertically so the screen looks alive even between rolls.
    const ballX = w / 2 + Math.sin(t * 1.4) * (w * 0.25);
    const ballY = h * 0.62 - Math.abs(Math.sin(t * 3.2)) * 24;
    ctx.fillStyle = '#3399ff';
    ctx.shadowColor = '#3399ff';
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(ballX, ballY, 38, 0, Math.PI * 2);
    ctx.fill();
    // Finger holes (three small dark circles at the top of the ball).
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#001a44';
    ctx.beginPath();
    ctx.arc(ballX - 12, ballY - 10, 5, 0, Math.PI * 2);
    ctx.arc(ballX + 12, ballY - 10, 5, 0, Math.PI * 2);
    ctx.arc(ballX, ballY + 6, 5, 0, Math.PI * 2);
    ctx.fill();

    // Footer — "▶ PLAY ◀" blinking on/off.
    const playAlpha = 0.45 + 0.55 * Math.max(0, Math.sin(t * 2.3));
    ctx.font = 'bold 60px "Courier New", monospace';
    ctx.fillStyle = `rgba(255, 90, 90, ${playAlpha})`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = '#FF5A5A';
    ctx.shadowBlur = 22 * playAlpha;
    ctx.fillText('▶  PLAY  ◀', w / 2, h * 0.86);
    ctx.shadowBlur = 0;

    // Scanline.
    const scanY = (t * 95) % h;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(0, scanY, w, 8);

    if (this.ghostScreenTex) this.ghostScreenTex.needsUpdate = true;
  }

  /**
   * Procedural wood-grain texture for the lane planks (and the ghost
   * lanes). Draws into a 512×512 canvas: vertical gradient from dark
   * to light brown for the wood tone, vertical lines every 1/5 of the
   * width to suggest plank seams, horizontal noise lines for grain.
   * Returns a `THREE.CanvasTexture` ready for `repeat.set(...)` to
   * tile it down the lane length.
   *
   * Why procedural and not a real wood texture: the GLB-based scene
   * already pulls ~14 MB of assets; adding a real high-res wood
   * tile would push that further with marginal visual benefit at the
   * camera distance.
   */
  private buildWoodTexture(): any {
    const THREE = this.THREE;
    const size = 512;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 0, 0, size);
    grad.addColorStop(0, '#7a4a1c');
    grad.addColorStop(1, '#a56a2a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const x = (size / 5) * i;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, size); ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 140; i++) {
      const y = Math.random() * size;
      const len = 40 + Math.random() * 300;
      const x = Math.random() * size;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.quadraticCurveTo(x + len / 2, y + (Math.random() - 0.5) * 4, x + len, y + (Math.random() - 0.5) * 2);
      ctx.stroke();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1, LANE_LENGTH / 3);
    tex.anisotropy = 4;
    return tex;
  }

  /**
   * Procedural fallback pin geometry — used when `pin.glb` fails to
   * load. Builds a `LatheGeometry` from a hand-crafted radial profile
   * that approximates the classic bowling-pin silhouette (head bulb,
   * narrow neck, body belly, tapered base).
   *
   * Profile points are normalised (radius 0..1, height 0..1) and
   * scaled to `PIN_RADIUS` / `PIN_HEIGHT` per-instance. Y is centred
   * around 0 (`-h/2 .. +h/2`) so the mesh's origin matches the cannon
   * body's centre — no offset needed when syncing the visual mesh.
   */
  private buildPinGeometry(): any {
    const THREE = this.THREE;
    // Bowling-pin silhouette via lathe: head bulb, narrow neck, body belly,
    // tapered base. Profile is normalised (radius 0..1, height 0..1) and then
    // scaled to PIN_RADIUS / PIN_HEIGHT. Y is centred (-h/2 to +h/2) so the
    // mesh sits at the physics body's centre with no offset needed.
    const profile: Array<[number, number]> = [
      [0.00, 0.00],
      [0.83, 0.00],
      [0.94, 0.04],
      [0.83, 0.10],
      [0.72, 0.20],
      [0.78, 0.32],
      [0.94, 0.44],
      [0.83, 0.54],
      [0.67, 0.62],
      [0.69, 0.72],
      [0.87, 0.80],
      [1.00, 0.88],
      [0.94, 0.95],
      [0.67, 1.00],
      [0.00, 1.00]
    ];
    const pts: any[] = profile.map(
      ([r, ty]) => new THREE.Vector2(r * PIN_RADIUS, (ty - 0.5) * PIN_HEIGHT)
    );
    return new THREE.LatheGeometry(pts, 28);
  }

  /**
   * Builds a single visual pin: prefers the loaded GLB model, otherwise
   * falls back to the procedural LatheGeometry pin so the game still runs
   * if the asset is missing or fails to load.
   *
   * The returned Group's origin sits at the pin's *centre* so the mesh can
   * be parented to the physics body (whose origin is also at the centre).
   */
  private buildPinVisual(pinTemplate: any | null): any {
    const THREE = this.THREE;
    const group = new THREE.Group();

    if (pinTemplate) {
      // The template's geometry was fully normalised by the engine at load
      // time: rotated to +Y-up, centred at origin in all three axes, and
      // scaled to a unit (1 m) height. So all we do here is clone, set the
      // per-instance scale to PIN_HEIGHT, and parent to a group at the
      // body's centre. The pin's bbox (post-scale) is `[-PIN_HEIGHT/2,
      // +PIN_HEIGHT/2]` in mesh-local; group sits at body.y = PIN_HEIGHT/2
      // → world bbox `[0, PIN_HEIGHT]`, base flush with the lane. No bbox
      // math, no chance of stale-cache or floating-point drift bugs.
      //
      // Mesh transform stays at identity rotation so body rotations (when
      // the pin falls) don't compound with a mesh rotation.
      const pin = pinTemplate.clone(true);
      pin.position.set(0, 0, 0);
      pin.rotation.set(0, 0, 0);
      pin.scale.setScalar(PIN_HEIGHT);
      pin.traverse((obj: any) => {
        if (obj.isMesh) {
          obj.castShadow = true;
          obj.receiveShadow = true;
        }
      });
      group.add(pin);
      return group;
    }

    // Fallback: procedural lathe pin + two red neck rings.
    const whiteMat = new THREE.MeshStandardMaterial({
      color: 0xfdfaf0, roughness: 0.28, metalness: 0.06,
      emissive: 0x1a1a26, emissiveIntensity: 0.15
    });
    const stripeMat = new THREE.MeshStandardMaterial({
      color: 0xe53935, roughness: 0.35, metalness: 0.08,
      emissive: 0x4a0c0c, emissiveIntensity: 0.3
    });
    const body = new THREE.Mesh(this.buildPinGeometry(), whiteMat);
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);
    const ringGeomA = new THREE.TorusGeometry(PIN_RADIUS * 0.79, 0.006, 8, 28);
    const ringGeomB = new THREE.TorusGeometry(PIN_RADIUS * 0.86, 0.006, 8, 28);
    for (let r = 0; r < 2; r++) {
      const ring = new THREE.Mesh(r === 0 ? ringGeomA : ringGeomB, stripeMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = (r === 0 ? 0.20 : 0.27) * PIN_HEIGHT;
      group.add(ring);
    }
    return group;
  }

  /**
   * Build all 10 main-lane pins — 10 visual meshes (`buildPinVisual`)
   * + 10 dynamic cannon bodies, one each per `PIN_LAYOUT` entry.
   * Stored in `this.pins[]` keyed by the layout's index 0..9.
   *
   * Pin physics body uses a Box, NOT a Cylinder — see the inline
   * comment below for the rationale (cannon Cylinder-vs-Plane has
   * a known contact bug that lets pins sink into the lane).
   *
   * Each pin's `spawn` pose is captured so `restorePinsFromMask`
   * can return it to the rack on the next round.
   */
  private buildPins(pinTemplate: any | null): void {
    const THREE = this.THREE;
    const CANNON = this.CANNON;

    // Use a Box (not a Cylinder) for the pin's physics shape.
    //
    // Why: cannon-es's `Cylinder` is a `ConvexPolyhedron` under the hood, and
    // its narrow-phase against `Plane` misses contacts on the cylinder's
    // bottom face — the body slowly sinks ~0.18 m through the lane before
    // enough vertices penetrate to register a contact. Diagnostic logging
    // confirmed body.y was settling at ~0.06 instead of PIN_HEIGHT/2 = 0.24,
    // dragging the visual mesh half-buried into the lane.
    //
    // A Box of half-extents (PIN_RADIUS, PIN_HEIGHT/2, PIN_RADIUS) has
    // bulletproof Box-Plane collision, gives the same knock-down behaviour
    // for bowling pins (which fall flat, they don't roll), and needs no
    // shape-level rotation since Box is axis-aligned in body-local. The
    // visual is still the GLB pin model — only the collision proxy changes.
    const pinShape = new CANNON.Box(
      new CANNON.Vec3(PIN_RADIUS, PIN_HEIGHT / 2, PIN_RADIUS)
    );

    PIN_LAYOUT.forEach((layout, i) => {
      const [row, col] = layout;
      const x = col * PIN_COL_SPACING;
      const z = PIN_HEAD_Z - row * PIN_ROW_SPACING;

      const group = this.buildPinVisual(pinTemplate);
      group.position.set(x, PIN_HEIGHT / 2, z);
      this.scene.add(group);

      const pinBody = new CANNON.Body({
        mass: PIN_MASS,
        position: new CANNON.Vec3(x, PIN_HEIGHT / 2, z),
        linearDamping: 0.22,
        angularDamping: 0.22,
        allowSleep: true,
        sleepSpeedLimit: 0.1,
        sleepTimeLimit: 0.5
      });
      pinBody.addShape(pinShape);
      this.world.addBody(pinBody);

      this.pins.push({
        mesh: group,
        body: pinBody,
        spawn: { x, y: PIN_HEIGHT / 2, z },
        knocked: false,
        idx: i
      });
    });
  }

  private buildBall(ballTemplate: any | null = null): void {
    const THREE = this.THREE;
    const CANNON = this.CANNON;

    let mesh: any;
    if (ballTemplate) {
      // GLB ball — engine has baked the geometry to a unit-bbox sphere
      // centred at origin, so a single uniform scale takes it to the
      // physics body's diameter (BALL_RADIUS * 2).
      mesh = ballTemplate;
      mesh.scale.setScalar(BALL_RADIUS * 2);
      mesh.castShadow = true;
    } else {
      // Fallback — procedural blue sphere, in case the GLB fails to load.
      const mat = new THREE.MeshStandardMaterial({
        color: 0x1a3da8,
        roughness: 0.12,
        metalness: 0.55,
        emissive: 0x0a1838,
        emissiveIntensity: 0.5
      });
      mesh = new THREE.Mesh(new THREE.SphereGeometry(BALL_RADIUS, 48, 36), mat);
      mesh.castShadow = true;
    }
    mesh.position.set(0, BALL_RADIUS, BALL_START_Z);
    this.scene.add(mesh);

    const body = new CANNON.Body({
      mass: BALL_MASS,
      shape: new CANNON.Sphere(BALL_RADIUS),
      position: new CANNON.Vec3(0, BALL_RADIUS, BALL_START_Z),
      linearDamping: 0.04,
      angularDamping: 0.04
    });
    this.world.addBody(body);

    this.ball = { mesh, body };

    // Pin-hit SFX. Cannon emits a `collide` event on the ball each
    // time it touches another body; we filter to "first contact with
    // a pin in this roll" so a strike doesn't fire ten overlapping
    // sounds. The flag resets at each `launch()`.
    body.addEventListener('collide', (event: any) => {
      if (this.pinContactPlayed) return;
      const otherBody = event?.body;
      if (!otherBody) return;
      const isPin = this.pins.some(p => p.body === otherBody);
      if (!isPin) return;
      this.pinContactPlayed = true;
      // Audio is a UI side-effect — go through ngZone so any audio-
      // service `BehaviorSubject` updates land in Angular's CD pass.
      this.ngZone.run(() => {
        // Cut the rolling rumble at the moment of impact, then play
        // the pin-hit one-shot. Order matters — stop first so the two
        // sounds don't briefly overlap.
        this.gameAudioService.stop(this.rollingAudio);
        this.rollingAudio = null;
        this.gameAudioService.play('assets/game-mode/sound-effects/bowlingPinHit.mp3');
        // Once the player has actually hit a pin, they've grasped the
        // mechanic — dismiss the on-lane hints permanently (until
        // they reopen the How-to-Play modal). Persist so the dismissal
        // survives reload.
        if (this.hintsActive) {
          this.hintsActive = false;
          try {
            localStorage.setItem(BowlingSceneComponent.HINTS_STORAGE_KEY, '1');
          } catch { /* ignore */ }
        }
      });
    });
  }

  // ──────────────────────────────────────────────────────────
  // Pointer-on-ball drag-to-aim, hold-to-charge, release-to-bowl
  // ──────────────────────────────────────────────────────────

  /**
   * Two-step input. The first pointer-down/up gesture **positions** the
   * ball (drag updates `aimX`, release locks it). A subsequent pointer-down
   * starts the **charge** phase — the power bar fills, release fires. The
   * `positioned` flag separates the two phases and resets on every rack.
   */
  private onPointerDown = (ev: PointerEvent) => {
    if (!this.loaded || this.rolling || this.holding || this.inputDisabled) return;
    ev.preventDefault();
    this.canvasRef.nativeElement.setPointerCapture?.(ev.pointerId);
    this.canvasRef.nativeElement.classList.add('grabbing');
    this.dragging = true;
    this.dragPointerId = ev.pointerId;

    if (this.positioned) {
      // Step 2: hold-to-charge. Don't snap aim to the pointer — preserve
      // the position the player chose in step 1. Drag during charge can
      // still fine-tune via onPointerMove.
      this.charging = true;
      this.chargeLevel = 0;
      this.chargeStartTime = performance.now();
      this.startChargeTick();
    } else {
      // Step 1: drag-to-position. Snap aim to pointer X immediately.
      this.aimX = this.projectPointerToLaneX(ev.clientX, ev.clientY);
    }
  };

  private onPointerMove = (ev: PointerEvent) => {
    if (!this.dragging || ev.pointerId !== this.dragPointerId) return;
    // In step 1 this is the primary positioning input. In step 2 (charging)
    // it's a fine-tune — drag updates aim while the bar continues filling.
    this.aimX = this.projectPointerToLaneX(ev.clientX, ev.clientY);
  };

  private onPointerUp = (ev: PointerEvent) => {
    if (!this.dragging || ev.pointerId !== this.dragPointerId) return;
    this.canvasRef.nativeElement.releasePointerCapture?.(ev.pointerId);

    if (this.charging) {
      // Step 2 release: fire the shot at the current charge level.
      this.fireShot();
    } else {
      // Step 1 release: lock the chosen position; next pointer-down will
      // enter the charge phase.
      this.canvasRef.nativeElement.classList.remove('grabbing');
      this.dragging = false;
      this.dragPointerId = null;
      this.positioned = true;
    }
  };

  private onPointerCancel = (ev: PointerEvent) => {
    if (ev.pointerId !== this.dragPointerId) return;
    this.canvasRef.nativeElement.releasePointerCapture?.(ev.pointerId);
    this.canvasRef.nativeElement.classList.remove('grabbing');
    // System-cancelled gesture (e.g. iOS swipe-from-edge): abort without
    // launching so the player isn't punished for an OS-level interrupt.
    // Don't reset `positioned` — the player keeps their chosen position
    // and can retry the gesture they were attempting.
    this.dragging = false;
    this.dragPointerId = null;
    this.stopChargeTick();
    this.charging = false;
    this.chargeLevel = 0;
  };

  /**
   * Fires the shot at the current charge level. Shared by manual release
   * and the auto-foul that triggers when the bar reaches 100%.
   */
  private fireShot = (): void => {
    if (!this.charging) return;
    this.stopChargeTick();
    const charge = this.chargeLevel;
    const power = this.computeShotPower(charge);
    this.canvasRef.nativeElement.classList.remove('grabbing');
    this.charging = false;
    this.dragging = false;
    this.dragPointerId = null;
    this.launch(this.aimX, power);
    // Reset bar + positioning so the next bowler starts cleanly in step 1
    // when the parent racks for the next turn.
    this.chargeLevel = 0;
    this.positioned = false;
  };

  /**
   * Mapping from bar fill (0..1) to a shot result.
   *  - sweet spot (80%–95%): perfect — disproportionate bonus speed.
   *  - past sweet spot but <100%: speed declines from MAX_NORMAL toward 40%
   *    of MAX_NORMAL, telegraphed by the red zone.
   *  - exactly 100%: bar broke — foul, very weak.
   *  - below sweet spot: linear ramp from MIN_SPEED to MAX_NORMAL.
   */
  private computeShotPower(charge: number): { speed: number; perfect: boolean; foul: boolean } {
    if (charge >= 1.0) {
      return { speed: SHOT_FOUL_SPEED, perfect: false, foul: true };
    }
    if (charge >= SWEET_SPOT_LO && charge <= SWEET_SPOT_HI) {
      return { speed: SHOT_PERFECT_SPEED, perfect: true, foul: false };
    }
    if (charge > SWEET_SPOT_HI) {
      const t = (charge - SWEET_SPOT_HI) / (1.0 - SWEET_SPOT_HI);
      const speed = SHOT_MAX_NORMAL_SPEED * (1 - t * 0.6);
      return { speed, perfect: false, foul: false };
    }
    const t = charge / SWEET_SPOT_LO;
    const speed = SHOT_MIN_SPEED + t * (SHOT_MAX_NORMAL_SPEED - SHOT_MIN_SPEED);
    return { speed, perfect: false, foul: false };
  }

  private startChargeTick(): void {
    if (this.chargeRafId !== null) return;
    const tick = () => {
      if (!this.charging) {
        this.chargeRafId = null;
        return;
      }
      const elapsed = performance.now() - this.chargeStartTime;
      const level = Math.min(1.0, elapsed / BAR_FILL_MS);
      this.chargeLevel = level;
      if (level >= 1.0) {
        // Overcharge — bar "breaks", auto-fire as foul.
        this.chargeRafId = null;
        this.fireShot();
        return;
      }
      this.chargeRafId = requestAnimationFrame(tick);
    };
    this.chargeRafId = requestAnimationFrame(tick);
  }

  private stopChargeTick(): void {
    if (this.chargeRafId !== null) {
      cancelAnimationFrame(this.chargeRafId);
      this.chargeRafId = null;
    }
  }

  /**
   * Wire the four pointer events on the canvas — `pointerdown`,
   * `pointermove`, `pointerup`, `pointercancel`. Handlers are
   * declared as **arrow-function fields** so `this` is bound and we
   * can pass them directly to `addEventListener` without wrappers.
   * Called from `ngAfterViewInit` after async load completes.
   */
  private attachPointerHandlers(): void {
    const c = this.canvasRef.nativeElement;
    c.addEventListener('pointerdown', this.onPointerDown);
    c.addEventListener('pointermove', this.onPointerMove);
    c.addEventListener('pointerup', this.onPointerUp);
    c.addEventListener('pointercancel', this.onPointerCancel);
  }

  /** Reverse of `attachPointerHandlers` — called from `ngOnDestroy`. */
  private detachPointerHandlers(): void {
    const c = this.canvasRef.nativeElement;
    c.removeEventListener('pointerdown', this.onPointerDown);
    c.removeEventListener('pointermove', this.onPointerMove);
    c.removeEventListener('pointerup', this.onPointerUp);
    c.removeEventListener('pointercancel', this.onPointerCancel);
  }

  /**
   * Map pointer X across the canvas linearly to a clamped lane X. We don't
   * use the ball-plane raycast for aim because pointers near the top of the
   * canvas project very far down the lane (or miss the plane entirely),
   * which makes the ball jump unpredictably.
   */
  private projectPointerToLaneX(clientX: number, _clientY: number): number {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const maxX = LANE_WIDTH / 2 - BALL_RADIUS - 0.02;
    return (t * 2 - 1) * maxX;
  }

  // ──────────────────────────────────────────────────────────
  // Launch + camera + settle
  // ──────────────────────────────────────────────────────────

  private launch(startX: number, power: { speed: number; perfect: boolean; foul: boolean }): void {
    if (!this.loaded || this.rolling) return;
    const speed = power.speed;
    // Defensive wake-up. The ball body doesn't have allowSleep enabled,
    // but the world does (`world.allowSleep = true`). Cannon ignores
    // velocity assignments on sleeping bodies — if the ball ever slips
    // into a sleeping state (e.g. via a re-enabled config or an idle
    // collision-pair), the launch below would silently no-op and the
    // 8 s rollDeadline would fire as a forced "GUTTER" with 0 pins
    // knocked. wakeUp() is a no-op on awake bodies — cheap and safe.
    this.ball.body.wakeUp();
    this.ball.body.position.set(startX, BALL_RADIUS, BALL_START_Z);
    this.ball.body.velocity.set(0, 0, -speed);
    this.ball.body.angularVelocity.set(-speed / BALL_RADIUS, 0, 0);
    this.ball.body.quaternion.set(0, 0, 0, 1);
    this.rolling = true;
    this.holding = false;
    this.settleStableFrames = 0;
    this.rollDeadline = performance.now() + 8000;
    this.camState = 'follow';
    // Reset pin-hit-SFX gate so the next roll can fire it once.
    this.pinContactPlayed = false;
    // Start the looping ball-roll SFX. Looped because a single roll
    // takes ~1–3 s and the source clip is shorter; we stop it
    // explicitly on first pin contact (or on settle, for gutter
    // balls) so the loop cut is intentional and matches the impact.
    this.ngZone.run(() => {
      this.gameAudioService.stop(this.rollingAudio);
      this.rollingAudio = this.gameAudioService.play(
        'assets/game-mode/sound-effects/bowlingBallRoll.mp3',
        // Custom gain — the source clip is mastered quieter than the
        // pin-hit / banner SFX. The default LOOP_GAIN of 0.55 made it
        // inaudible (~0.11 volume at the default master 0.2); a first
        // bump to 2.5 was still too quiet per user feedback. 4.0
        // gives the rolling rumble a clearly-present rumble — at
        // master 0.2 → volume 0.8; at master 0.25+ it caps at 1.0.
        { loop: true, gain: 4.0 }
      );
      this.rollStarted.emit();
    });
  }

  private setIdleCamera(): void {
    // Low, just behind the ball — gives a clear over-the-shoulder shot of
    // ball + lane + pins. Look at a point partway down the lane so the ball
    // sits in the lower third of frame and the pins read at the upper third.
    this.camera.position.set(0, 0.95, BALL_START_Z + 2.4);
    this.camera.lookAt(0, 0.35, -2.0);
  }

  private updateFollowCamera(): void {
    const ballPos = this.ball.body.position;
    // Clamp the ball reference position so the camera doesn't dive into
    // the pit when the ball drops, and doesn't slide forward past the
    // pin deck when the ball rolls into the pit.
    //   - Y: clamp to lane top (0). If ball is on lane (y≈0.16) follow
    //     normally; if ball drops (y=-0.5) hold reference at 0.
    //   - Z: clamp to PIN_HEAD_Z + 1.5. The camera target is then
    //     PIN_HEAD_Z + 1.5 + 2.2 ≈ PIN_HEAD_Z + 3.7, so the camera always
    //     stays at least 3.7 m in front of the front pin row — never
    //     ends up sitting next to or between pins.
    //   - Final Y: at least 1.5 m so the camera is comfortably above
    //     the pin tops (0.48 m).
    const ballRefY = Math.max(ballPos.y, 0);
    const ballRefZ = Math.max(ballPos.z, PIN_HEAD_Z + 1.5);
    const targetX = ballPos.x;
    const targetY = Math.max(ballRefY + 0.95, 1.5);
    const targetZ = ballRefZ + 2.2;
    this.camera.position.x += (targetX - this.camera.position.x) * 0.2;
    this.camera.position.y += (targetY - this.camera.position.y) * 0.18;
    this.camera.position.z += (targetZ - this.camera.position.z) * 0.18;
    // LookAt fixed at the pin deck — when the ball is in the pit the
    // camera should be staring at the pin deck, not the empty space
    // beyond the back wall.
    this.camera.lookAt(ballPos.x, 0.25, PIN_HEAD_Z - 0.5);
  }

  private updateHoldCamera(): void {
    // Settle into a tight pin-deck framing for the impact hold. Lerp
    // factor bumped from 0.06 → 0.18 so the framing actually lands
    // inside the 1.4 s hold window instead of taking the full window
    // to get there.
    this.camera.position.x += (0 - this.camera.position.x) * 0.18;
    this.camera.position.y += (1.0 - this.camera.position.y) * 0.18;
    this.camera.position.z += ((PIN_HEAD_Z + 2.4) - this.camera.position.z) * 0.18;
    this.camera.lookAt(0, PIN_HEIGHT * 0.5, PIN_HEAD_Z - 0.3);
  }

  // ──────────────────────────────────────────────────────────
  // Main loop
  // ──────────────────────────────────────────────────────────

  private startLoop(): void {
    this.ngZone.runOutsideAngular(() => {
      this.lastFrameTime = performance.now();
      const tick = () => {
        if (this.disposed) return;
        this.rafId = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.05, (now - this.lastFrameTime) / 1000);
        this.lastFrameTime = now;

        // Parent requested a fresh rack (between turns) — restore standing pins
        // and put the ball back on the approach.
        if (this.rackTick !== this.lastRackTick && this.loaded) {
          this.lastRackTick = this.rackTick;
          this.resetBall();
          this.restorePinsFromMask(this.pinsStanding);
          this.camState = 'idle';
          this.holding = false;
          this.positioned = false;
          this.charging = false;
          this.chargeLevel = 0;
          this.stopChargeTick();
          this.setIdleCamera();
        }

        this.world.step(1 / 60, dt, 3);

        // Idle / drag: ball stays on the approach. While the user drags, the
        // ball follows the projected pointer X (visualises aim). The clamp
        // runs **before** `syncMeshesFromBodies` so the visual mesh
        // copies the clamped values, not the (just-stepped) post-gravity
        // ones — without this ordering the mesh shows a 1–3 mm drop per
        // frame that reads as a vertical jitter.
        if (!this.rolling) {
          const targetX = this.dragging
            ? this.aimX
            : this.ball.body.position.x; // hold position when not dragging
          this.ball.body.position.x += (targetX - this.ball.body.position.x) * 0.4;
          // y pinned to exact rest contact (lane physics top is at y=0,
          // ball radius 0.16). The previous +0.01 offset parked the ball
          // 1 cm in mid-air every frame — gravity yanked it down each
          // step, the clamp lifted it back, and the resulting micro-
          // oscillation was the visible "shake".
          this.ball.body.position.y = BALL_RADIUS;
          this.ball.body.position.z = BALL_START_Z;
          this.ball.body.velocity.set(0, 0, 0);
          this.ball.body.angularVelocity.set(0, 0, 0);
        }

        this.syncMeshesFromBodies();

        // Camera state machine.
        if (this.camState === 'follow') {
          this.updateFollowCamera();
        } else if (this.camState === 'hold') {
          this.updateHoldCamera();
        }

        if (this.rolling) {
          this.containBallToLane();
          this.checkSettle(now);
        }
        if (this.holding && now >= this.holdUntil) this.finishHold();

        // Score-panel animation. Throttled to ~120 ms (≈ 8 fps) — the
        // animated bits (border pulse, LIVE blink, scanline scroll) read
        // smooth at this rate and we save 50+ canvas redraws per second.
        if (now - this.lastScorePanelRedraw > 120) {
          this.redrawScorePanel();
          this.lastScorePanelRedraw = now;
        }
        // Ghost-lane screen-saver animation. Slightly slower throttle
        // (~150 ms) — the bouncing-ball / wash effects read smooth and
        // it's a single shared canvas updating four meshes.
        if (now - this.lastGhostScreenRedraw > 150) {
          this.redrawGhostScreen();
          this.lastGhostScreenRedraw = now;
        }

        this.renderer.render(this.scene, this.camera);
      };
      tick();
    });
  }

  /**
   * Copy each cannon body's pose (position + quaternion) onto its
   * Three.js mesh — the per-frame "physics drives visuals" link.
   * Called once per render frame from the main loop, AFTER any
   * idle clamps so the visual state always matches the clamped /
   * stepped body state.
   *
   * Both ball and pin meshes have their group origin == body centre,
   * so a direct `position.copy` / `quaternion.copy` works without
   * any offset math.
   */
  private syncMeshesFromBodies(): void {
    this.ball.mesh.position.copy(this.ball.body.position as any);
    this.ball.mesh.quaternion.copy(this.ball.body.quaternion as any);
    for (const p of this.pins) {
      // Mesh group origin = body centre — both copy directly.
      p.mesh.position.copy(p.body.position as any);
      p.mesh.quaternion.copy(p.body.quaternion as any);
    }
  }

  /**
   * Safety net: if the ball ever escapes the wall envelope (tunnels through a
   * wall, gets launched skyward by a freak pin collision, etc.), snap it back
   * inside lane bounds so it can't roll off into the void on the infinite
   * ground plane. The tall walls should normally prevent this; this exists so
   * a single missed collision can't ruin the round.
   */
  private containBallToLane(): void {
    const b = this.ball.body;
    const xMax = LANE_WIDTH / 2 + GUTTER_WIDTH + 0.05;
    // Y allows for the pit floor (y=-PIT_DEPTH) plus headroom; Z extends
    // past the back of the pit so a ball legitimately landing in the pit
    // isn't snapped back to the lane.
    const escaped =
      Math.abs(b.position.x) > xMax ||
      b.position.y > 3.0 ||
      b.position.y < -PIT_DEPTH - 0.5 ||
      b.position.z > BALL_START_Z + 2.0 ||
      b.position.z < PIT_BACK_Z - 0.3;
    if (!escaped) return;
    // Drop the ball at the gutter edge on the side it escaped from, near the
    // back of the lane — it's effectively a gutter ball at this point.
    const sign = b.position.x >= 0 ? 1 : -1;
    b.position.set(sign * (xMax - 0.1), BALL_RADIUS, PIN_HEAD_Z - 0.5);
    b.velocity.set(0, 0, 0);
    b.angularVelocity.set(0, 0, 0);
  }

  private checkSettle(now: number): void {
    const b = this.ball.body;
    // Ball threshold loosened from 0.15 → 0.35 m/s. At 0.15 the ball
    // had to crawl to a near-complete stop before being declared settled
    // — visually stationary but logically still rolling, padding the
    // post-impact dead time.
    const ballSettled =
      b.velocity.length() < 0.35 ||
      b.position.z < PIN_HEAD_Z - 1.2 ||
      Math.abs(b.position.x) > LANE_WIDTH / 2 + GUTTER_WIDTH;

    // Knocked pins (lying on their side or below the lane) skip the
    // rest check — once a pin is down, its lingering wobble shouldn't
    // gate the whole roll. Standing pins use slightly looser thresholds
    // (vel 0.2 → 0.35, ang 0.4 → 1.0) so a barely-teetering pin doesn't
    // either.
    let pinsAtRest = true;
    for (const p of this.pins) {
      if (this.isPinDown(p)) continue;
      if (p.body.velocity.length() > 0.35 || p.body.angularVelocity.length() > 1.0) {
        pinsAtRest = false;
        break;
      }
    }

    if ((ballSettled && pinsAtRest) || now > this.rollDeadline) {
      this.settleStableFrames++;
      // Stable-frame requirement cut from 10 → 5 (~83 ms at 60 fps).
      // Once the loosened thresholds say "settled", trust them.
      if (this.settleStableFrames > 5 || now > this.rollDeadline) {
        this.beginHold(now);
      }
    } else {
      this.settleStableFrames = 0;
    }
  }

  private beginHold(now: number): void {
    this.rolling = false;
    this.holding = true;
    this.holdUntil = now + HOLD_AFTER_SETTLE_MS;
    this.camState = 'hold';

    // Fallback: cut the rolling-rumble SFX if it's still playing — the
    // collide listener above stops it on first pin contact, but a
    // gutter ball (no pin contact) has no other stop trigger and would
    // otherwise loop forever.
    if (this.rollingAudio) {
      this.gameAudioService.stop(this.rollingAudio);
      this.rollingAudio = null;
    }

    // Emit rollComplete *now* (when pins are at rest) instead of after
    // the camera hold — the parent's banner appears immediately and the
    // hold + banner display + rack-up all run in parallel inside the
    // same 1.4 s window. Pins are stationary at this point, so the
    // knocked-status check returns the same result it would after the
    // hold.
    const knockedIndices: number[] = [];
    for (const p of this.pins) {
      if (p.knocked) continue;
      if (this.isPinDown(p)) {
        p.knocked = true;
        knockedIndices.push(p.idx);
      }
    }
    this.ngZone.run(() =>
      this.rollComplete.emit({ pinsKnocked: knockedIndices.length, knockedIndices })
    );
  }

  private finishHold(): void {
    // Camera back to bowler view. Knocked-pin emission already happened
    // in beginHold; this is purely the post-hold camera reset.
    this.holding = false;
    this.camState = 'idle';
    this.setIdleCamera();
  }

  /**
   * Is this pin "knocked"? Two conditions, either alone is enough:
   *
   *   1. **Below the lane** (`body.position.y < 0.05`) — pin has
   *      fallen into the pit.
   *   2. **Tilted past 50°** — the pin's local +Y axis (its top),
   *      rotated by the body quaternion, has a dot product with the
   *      world +Y axis below 0.65. `cos(50°) ≈ 0.643`, so 0.65
   *      means roughly "tipped more than 49°".
   *
   * Used by both `beginHold`'s knocked-pin scan (to count the roll's
   * result) and `checkSettle`'s pinsAtRest scan (knocked pins are
   * skipped — a wobbling downed pin shouldn't gate settle).
   */
  private isPinDown(p: Pin): boolean {
    if (p.body.position.y < 0.05) return true;
    const up = new this.CANNON.Vec3(0, 1, 0);
    const pinUp = new this.CANNON.Vec3(0, 1, 0);
    p.body.quaternion.vmult(pinUp, pinUp);
    return up.dot(pinUp) < 0.65;
  }

  /**
   * Hard-reset the ball back to its idle pose at the front of the
   * lane. Wakes the body first — sleeping cannon bodies ignore
   * `velocity.set` writes, which would otherwise leave the ball at
   * its pre-rack position on the next launch (manifest as the bug
   * fixed by the wakeUp commit on 2026-05-07).
   */
  private resetBall(): void {
    // wakeUp() before mutating state — sleeping bodies ignore velocity
    // writes in cannon, which would otherwise leave the ball at its
    // pre-rack position on the next launch.
    this.ball.body.wakeUp();
    this.ball.body.velocity.set(0, 0, 0);
    this.ball.body.angularVelocity.set(0, 0, 0);
    this.ball.body.position.set(0, BALL_RADIUS, BALL_START_Z);
    this.ball.body.quaternion.set(0, 0, 0, 1);
    this.aimX = 0;
  }

  /**
   * Apply the host-supplied 10-bool standing mask to the pins:
   *
   *   - `mask[i] === true`  → restore pin i to its spawn pose,
   *                           wake the body, show the mesh.
   *   - `mask[i] === false` → drop pin i to y=-10 (out of frustum),
   *                           hide the mesh, sleep the body so it
   *                           doesn't waste CPU during simulation.
   *
   * Called in the main loop on every `rackTick` change. In our
   * model the mask is always all-true (each roll faces a fresh
   * rack) but the multi-roll-frame plumbing is preserved here for
   * future expansion.
   */
  private restorePinsFromMask(mask: boolean[]): void {
    this.pins.forEach((p, i) => {
      const standing = mask[i];
      p.knocked = !standing;
      if (standing) {
        p.body.position.set(p.spawn.x, p.spawn.y, p.spawn.z);
        p.body.quaternion.set(0, 0, 0, 1);
        p.body.velocity.set(0, 0, 0);
        p.body.angularVelocity.set(0, 0, 0);
        p.body.wakeUp();
        p.mesh.visible = true;
      } else {
        p.body.position.set(p.spawn.x, -10, p.spawn.z);
        p.body.velocity.set(0, 0, 0);
        p.body.angularVelocity.set(0, 0, 0);
        p.body.sleep();
        p.mesh.visible = false;
      }
    });
  }

  private disposeScene(): void {
    // Renderer cleanup handles GPU resources for this renderer's
    // WebGL context. We do **not** traverse the scene and dispose
    // every geometry/material/texture — those are *shared* with the
    // cached `pinModelTemplate` / `ballModelTemplate` (and any
    // material/texture they reference) inside `BowlingEngineService`.
    // `clone(true)` in Three.js shares geometry + material refs by
    // default, so per-resource dispose() here would dispatch dispose
    // events on resources the next session needs to reuse — leading
    // to a blank scene when the game-world is reopened. The
    // scene-only procedural meshes (lane, walls, gutters etc.) are
    // garbage-collected when this component is destroyed and `this.scene`
    // is nulled below — no manual dispose needed.
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss?.();
      this.renderer = null;
    }
    this.scene = null;
    this.world = null;
    this.pins = [];
    this.targetArrows = [];
    this.ball = undefined as any;
  }
}
